import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { resolveUploadUrl } from '../../utils/apiClient';
import { getScheduleEmployees, updateScheduleEmployeeVisibility } from '../../services/scheduleService';
import { useCurrentUser } from '../../context/UserContext';
import { useCurrency } from '../../hooks/useCurrency';
import { usePermissions } from '../../hooks/usePermission';
import FormModal from "../../components/forms/FormModal";
import Toast from '../../components/Toast';
import ConfirmDialog from '../../components/ConfirmDialog';
import ClockTimePicker from '../../components/forms/ClockTimePicker';
import ColorPicker from '../../components/forms/ColorPicker';
import CustomDropdown from '../../components/forms/CustomDropdown';
import { createBooking, getBookings, deleteBooking, updateBooking, getActivityTypes, exportSchedule } from "../../services/scheduleService";
import { getAssignedMilestones, getProjects, getMilestones } from '../../services/projectService';
import { getEmployees, getProjectsForEmployee } from '../../services/peopleService';
import ProjectAssignmentModal from '../../components/schedule/ProjectAssignmentModal';
import DetailsModal from '../../components/forms/DetailsModal';
import { createTimeOffRequest, getTimeOffTypes, getTimeOffRequests, updateTimeOffRequest, cancelTimeOffRequest } from '../../services/timeOffService';
import { getNotificationCounts } from '../../services/notificationService';
import { createActivityType } from '../../services/activityTypeService';
import ApprovalList from '../../components/timeoff/ApprovalList';
import { usePendingApprovals } from '../../context/PendingApprovalsContext';
import { buildTimeOffCreatePayload, buildTimeOffUpdatePayload } from '../../utils/timeOffForm';
import { countLeaveDays, formatDays } from '../../utils/leaveDays';
import styles from './Schedule.module.css';
import ui from './DashboardUI.module.css';
import detailStyles from '../../components/forms/DetailsModal.module.css';
import useMinimumLoader from '../../hooks/useMinimumLoader';

const getMonday = (date) => {
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  // Create date with explicit year, month, day to avoid timezone issues
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + diff, 0, 0, 0, 0);
};

const getDates = (startDate, numDays) => {
  const dates = [];
  for (let i = 0; i < numDays; i++) {
    // Create date with explicit year, month, day to avoid timezone issues
    const d = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + i, 12, 0, 0, 0);
    dates.push(d);
  }
  return dates;
};

const toLocalISODate = (date) => {
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().split('T')[0];
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Load and cache avatar images
const loadAvatarImage = (avatarUrl, employeeId) => {
  return new Promise((resolve) => {
    if (!avatarUrl) {
      resolve(null);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous'; // Handle CORS
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null); // Fall back to initials if image fails
    img.src = avatarUrl;
  });
};

const parseColorToRgb = (color) => {
  if (!color || typeof color !== 'string') return null;

  const trimmed = color.trim();
  const hexMatch = trimmed.match(/^#?([a-fA-F0-9]{3}|[a-fA-F0-9]{6})$/);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) {
      hex = hex.split('').map((c) => c + c).join('');
    }
    return {
      r: parseInt(hex.substring(0, 2), 16),
      g: parseInt(hex.substring(2, 4), 16),
      b: parseInt(hex.substring(4, 6), 16),
    };
  }

  const rgbMatch = trimmed.match(/^rgba?\(([^)]+)\)$/i);
  if (rgbMatch) {
    const values = rgbMatch[1].split(',').map((v) => Number(v.trim()));
    if (values.length >= 3 && values.slice(0, 3).every((v) => Number.isFinite(v))) {
      return {
        r: Math.max(0, Math.min(255, values[0])),
        g: Math.max(0, Math.min(255, values[1])),
        b: Math.max(0, Math.min(255, values[2])),
      };
    }
  }

  return null;
};

const rgbaFromColor = (rgb, alpha, fallback) => {
  if (!rgb) return fallback;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

const getVisibleItemSpan = (itemStartRaw, itemEndRaw, visibleDates) => {
  if (!visibleDates || visibleDates.length === 0) return null;

  const itemStart = new Date(itemStartRaw);
  const itemEnd = new Date(itemEndRaw);
  if (Number.isNaN(itemStart.getTime()) || Number.isNaN(itemEnd.getTime())) return null;

  const toLocalDay = (dateLike) => {
    const d = new Date(dateLike);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  };

  const viewStart = toLocalDay(visibleDates[0]);
  const viewEnd = toLocalDay(visibleDates[visibleDates.length - 1]);
  const itemStartDay = toLocalDay(itemStart);
  const itemEndDay = toLocalDay(itemEnd);

  if (itemEndDay < viewStart || itemStartDay > viewEnd) {
    return null;
  }

  const clippedStart = itemStartDay < viewStart ? viewStart : itemStartDay;
  const clippedEnd = itemEndDay > viewEnd ? viewEnd : itemEndDay;
  const visibleDateKeys = visibleDates.map((d) => toLocalISODate(d));
  const startDayIndex = visibleDateKeys.indexOf(toLocalISODate(clippedStart));
  const endDayIndex = visibleDateKeys.indexOf(toLocalISODate(clippedEnd));

  if (startDayIndex === -1 || endDayIndex === -1 || endDayIndex < startDayIndex) {
    return null;
  }

  return {
    startDayIndex,
    endDayIndex,
    spanDays: endDayIndex - startDayIndex + 1,
  };
};

const getDurationDays = (startRaw, endRaw) => {
  const start = new Date(startRaw);
  const end = new Date(endRaw);
  return Math.max(1, Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1);
};

const sortItemsByDurationDesc = (items) => {
  return [...items].sort((a, b) => getDurationDays(b.start_datetime, b.end_datetime) - getDurationDays(a.start_datetime, a.end_datetime));
};

const assignItemsToRows = (items) => {
  const rows = [];
  items.forEach((item) => {
    let assignedRow = -1;
    for (let r = 0; r < rows.length; r++) {
      const overlaps = rows[r].some((existingItem) => {
        const existingStart = new Date(existingItem.start_datetime);
        const existingEnd = new Date(existingItem.end_datetime);
        const itemStart = new Date(item.start_datetime);
        const itemEnd = new Date(item.end_datetime);
        return itemStart < existingEnd && itemEnd > existingStart;
      });
      if (!overlaps) {
        assignedRow = r;
        break;
      }
    }
    if (assignedRow === -1) {
      assignedRow = rows.length;
      rows.push([]);
    }
    rows[assignedRow].push(item);
  });
  return rows;
};

const drawBookingBlock = (ctx, booking, bookingX, bookingY, bookingWidth, isHovered, BOOKING_HEIGHT, RESOURCE_ROW_HEIGHT) => {
  const hasAllocatedResource = !!booking.resource_id;
  const resourceStripHeight = hasAllocatedResource ? RESOURCE_ROW_HEIGHT : 0;
  const mainBlockHeight = BOOKING_HEIGHT;

  // Get base color and derive tints for soft card styling.
  const baseColor = booking.activity_type_color || booking.project_color || '#3b82f6';
  const rgb = parseColorToRgb(baseColor);
  const cardBackground = rgbaFromColor(rgb, isHovered ? 0.24 : 0.18, isHovered ? 'rgba(148, 163, 184, 0.24)' : 'rgba(148, 163, 184, 0.18)');
  const cardBorder = rgbaFromColor(rgb, isHovered ? 0.52 : 0.36, isHovered ? 'rgba(100, 116, 139, 0.52)' : 'rgba(100, 116, 139, 0.36)');

  ctx.shadowColor = isHovered ? 'rgba(15, 23, 42, 0.22)' : 'rgba(15, 23, 42, 0.12)';
  ctx.shadowBlur = isHovered ? 7 : 4;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = isHovered ? 3 : 1;

  ctx.fillStyle = cardBackground;
  ctx.beginPath();
  ctx.roundRect(bookingX, bookingY, bookingWidth, mainBlockHeight, 6);
  ctx.fill();

  ctx.strokeStyle = cardBorder;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(bookingX, bookingY, bookingWidth, mainBlockHeight, 6);
  ctx.stroke();

  // Left accent strip matches the project/activity color.
  ctx.fillStyle = baseColor;
  ctx.fillRect(bookingX + 1, bookingY + 1, 4, Math.max(0, mainBlockHeight - 2));

  // Reset shadow for text
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;

  const textLeft = bookingX + 10;
  const textWidth = bookingWidth - 20;

  ctx.save();
  ctx.beginPath();
  ctx.rect(textLeft, bookingY + 1, Math.max(10, textWidth), Math.max(8, mainBlockHeight - 2));
  ctx.clip();

  const companyText = booking.company_name || 'No Company';
  const clientText = booking.client_name || 'No Client';
  const projectText = booking.project_name || booking.activity_type_name || 'No Project';

  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  ctx.font = '700 10px Inter, system-ui, sans-serif';
  ctx.fillStyle = '#0f172a';
  ctx.fillText(companyText, textLeft, bookingY + 6);

  ctx.font = '500 9px Inter, system-ui, sans-serif';
  ctx.fillStyle = '#334155';
  ctx.fillText(clientText, textLeft, bookingY + 18);

  ctx.font = '700 9px Inter, system-ui, sans-serif';
  ctx.fillStyle = '#1e293b';
  ctx.fillText(projectText, textLeft, bookingY + 29);

  ctx.font = '600 8px Inter, system-ui, sans-serif';
  ctx.fillStyle = '#1f2937';
  ctx.textAlign = 'right';

  const startDate = new Date(booking.start_datetime);
  const endDate = new Date(booking.end_datetime);
  const daysDiff = Math.max(1, Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1);
  const hoursText = `${booking.hours_allocated / daysDiff}h${daysDiff > 1 ? ` × ${daysDiff}d` : ''}`;

  ctx.fillText(hoursText, bookingX + bookingWidth - 10, bookingY + mainBlockHeight - 11);
  ctx.restore();

  // Dedicated attached strip for allocated resources
  if (hasAllocatedResource) {
    const stripY = bookingY + mainBlockHeight + 2;
    ctx.fillStyle = rgbaFromColor(rgb, 0.16, 'rgba(191, 219, 254, 0.9)');
    ctx.beginPath();
    ctx.roundRect(bookingX + 1, stripY, bookingWidth - 2, resourceStripHeight, 4);
    ctx.fill();

    ctx.strokeStyle = rgbaFromColor(rgb, 0.34, 'rgba(15, 23, 42, 0.22)');
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(bookingX + 1, stripY, bookingWidth - 2, resourceStripHeight, 4);
    ctx.stroke();

    // Small leading marker to make resource-linked bookings easy to scan.
    const markerX = bookingX + 6;
    const markerY = stripY + Math.floor(resourceStripHeight / 2);
    ctx.fillStyle = 'rgba(15, 23, 42, 0.72)';
    ctx.beginPath();
    ctx.arc(markerX, markerY, 2, 0, Math.PI * 2);
    ctx.fill();
 

    const resourceEmoji = booking.resource_type === 'vehicle' ? '🚗' : booking.resource_type === 'tool' ? '🔧' : '📦';
    const rawResourceText = booking.resource_name ? `${resourceEmoji} ${booking.resource_name}` : `${resourceEmoji} Resource`;

    const maxTextWidth = Math.max(24, bookingWidth - 20);
    ctx.font = '600 9px Inter, system-ui, sans-serif';

    const truncateToWidth = (text, width) => {
      if (ctx.measureText(text).width <= width) return text;
      const ellipsis = '…';
      if (ctx.measureText(ellipsis).width > width) return '';

      const source = text;
      for (let i = source.length - 1; i >= 0; i--) {
        const candidate = `${source.slice(0, i)}${ellipsis}`;
        if (ctx.measureText(candidate).width <= width) {
          return candidate;
        }
      }
      return '';
    };

    const resourceText = truncateToWidth(rawResourceText, maxTextWidth);

    ctx.fillStyle = 'rgba(15, 23, 42, 0.86)';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    if (resourceText) {
      ctx.fillText(resourceText, bookingX + 12, bookingY + mainBlockHeight + 2 + Math.floor(resourceStripHeight / 2));
    }

    // Restore defaults used by caller-side draw flow
    ctx.textBaseline = 'top';
  }
};

const drawTimeOffBlock = (ctx, timeOff, timeOffX, timeOffY, timeOffWidth, isHovered, BOOKING_HEIGHT) => {
  const isPending = timeOff.status === 'pending';
  
  // Subtle shadow for depth (only for approved requests)
  if (!isPending) {
    ctx.shadowColor = isHovered ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.2)';
    ctx.shadowBlur = isHovered ? 6 : 3;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = isHovered ? 2 : 1;
  }

  // Black background for time off
  const gradient = ctx.createLinearGradient(
    timeOffX, timeOffY, 
    timeOffX + timeOffWidth, timeOffY + BOOKING_HEIGHT
  );
  gradient.addColorStop(0, '#000000');
  gradient.addColorStop(0.7, '#333333');
  gradient.addColorStop(1, '#666666');
  
  ctx.fillStyle = gradient;
  ctx.globalAlpha = isPending ? 0.6 : 1.0; // Lower opacity for pending
  ctx.beginPath();
  ctx.roundRect(timeOffX, timeOffY, timeOffWidth, BOOKING_HEIGHT, 4);
  ctx.fill();

  // White border
  ctx.strokeStyle = isPending ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.5)';
  ctx.lineWidth = 1;
  ctx.stroke();

  // Reset shadow and opacity
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.globalAlpha = 1.0;

  // White text for time off
  ctx.fillStyle = isPending ? 'rgba(255, 255, 255, 0.7)' : '#ffffff';
  ctx.font = '600 11px Inter, system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  
  // Clip text to block width with padding
  ctx.save();
  ctx.beginPath();
  ctx.rect(timeOffX + 10, timeOffY, timeOffWidth - 20, BOOKING_HEIGHT);
  ctx.clip();
  
  ctx.fillText(timeOff.type_name || 'Time Off', timeOffX + 10, timeOffY + 6);
  
  ctx.font = '300 9px Inter, system-ui, sans-serif';
  // ctx.fillText(timeOff.status || '', timeOffX + 10, timeOffY + 20);
  
  // Calculate days
  const startDate = new Date(timeOff.start_date);
  const endDate = new Date(timeOff.end_date);
  const daysDiff = Math.max(1, Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1);
  ctx.fillText(`${daysDiff} day${daysDiff > 1 ? 's' : ''}`, timeOffX + 10, timeOffY + 34);
  
  ctx.restore();
};

const DAY_OPTIONS = [
  { value: 7, label: '7 Days' },
  { value: 14, label: '14 Days' },
  { value: 28, label: '28 Days' },
];

const CanvasSchedule = () => {
  const currentUser = useCurrentUser();
  const { format: formatMoney } = useCurrency();
  const { hasPermission } = usePermissions();
  const { pendingCount, refreshPendingCount } = usePendingApprovals();
  
  // Permission checks  
  const canViewAllSchedules = hasPermission('view_schedule');
  const canViewOwnSchedule = hasPermission('view_own_schedule');
  const canCreateBookings = hasPermission('create_bookings');
  const canEditBookings = hasPermission('edit_bookings');
  const canDeleteBookings = hasPermission('delete_bookings');
  const canViewTimeOff = hasPermission('timeoff:view:self') || hasPermission('timeoff:view:all') || hasPermission('timeoff:approve');
  const canEditOwnPendingTimeOff = hasPermission('timeoff:edit:self:pending');
  const canEditTeamPendingTimeOff = hasPermission('timeoff:edit:team:pending');
  const canEditApprovedTimeOff = hasPermission('timeoff:edit:approved');
  const canApproveTimeOff = hasPermission('timeoff:approve');
  const canViewAllTimeOff = hasPermission('timeoff:view:all');
  const canManageProjectTeam = hasPermission('manage_project_team');

  // Combined time-off permission check
  const hasAnyTimeOffPermission = canViewTimeOff || canEditOwnPendingTimeOff || canEditTeamPendingTimeOff || canEditApprovedTimeOff || canApproveTimeOff;

  const canvasRef = useRef(null);
  const headerOverlayRef = useRef(null);
  const containerRef = useRef(null);
  const [employees, setEmployees] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(getMonday(new Date()));
  const [daysToShow, setDaysToShow] = useState(28);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [hoveredBooking, setHoveredBooking] = useState(null);

  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [draggedBooking, setDraggedBooking] = useState(null);
  const [draggedBookingRowIdx, setDraggedBookingRowIdx] = useState(null);
  const [dragOffsetX, setDragOffsetX] = useState(0);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [currentMouseX, setCurrentMouseX] = useState(0);
  const [currentMouseY, setCurrentMouseY] = useState(0);
  const [justDragged, setJustDragged] = useState(false);
  const [wasDraggingBooking, setWasDraggingBooking] = useState(false);
  const [toast, setToast] = useState(null);
  const [employeeMilestones, setEmployeeMilestones] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState("booking");
  const [showTimeOffModal, setShowTimeOffModal] = useState(false);
  const [timeOffTypes, setTimeOffTypes] = useState([]);
  const [showCancelTimeOffConfirm, setShowCancelTimeOffConfirm] = useState(false);
  const [timeOffToCancel, setTimeOffToCancel] = useState(null);
  const [avatarImages, setAvatarImages] = useState(new Map()); // Map<employeeId, { url, img }>
  const avatarLoadInFlightRef = useRef(new Set());
  const [showNewActivityTypeModal, setShowNewActivityTypeModal] = useState(false);
  const [newActivityTypeForm, setNewActivityTypeForm] = useState({
    name: "",
    description: "",
    color: "#3b82f6"
  });
  const [showTimeOffApprovalModal, setShowTimeOffApprovalModal] = useState(false);
  const [selectedTimeOffForApproval, setSelectedTimeOffForApproval] = useState(null);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [pendingTimeOffRequests, setPendingTimeOffRequests] = useState([]);
  const [pendingNotificationsCount, setPendingNotificationsCount] = useState(0);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [visibilityEmployees, setVisibilityEmployees] = useState([]);
  const [isVisibilityLoading, setIsVisibilityLoading] = useState(false);
  const [resources, setResources] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [showEmployeeDetailModal, setShowEmployeeDetailModal] = useState(false);
  const [selectedEmployeeDetail, setSelectedEmployeeDetail] = useState(null);
  const [employeeDetailTab, setEmployeeDetailTab] = useState('overview');
  const [employeeAssignedProjects, setEmployeeAssignedProjects] = useState([]);
  const [isEmployeeDetailLoading, setIsEmployeeDetailLoading] = useState(false);
  const [isEmployeeProjectsLoading, setIsEmployeeProjectsLoading] = useState(false);
  
  // Project assignment modal state
  const [showProjectAssignmentModal, setShowProjectAssignmentModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const showLoading = useMinimumLoader(loading);

  const [bookingForm, setBookingForm] = useState({
    bookingId: null,
    employeeId: "",
    resourceId: "",
    startDate: "",
    endDate: "",
    projectId: "",
    projectName: "",
    projectCompanyName: "",
    milestoneId: "",
    activityTypeId: "",
    hoursPerDay: 8,
    description: "",
    isTimeSpecific: false,
    startTime: "09:00",
    endTime: "17:00",
  });
  const [companyFilter, setCompanyFilter] = useState("");

  const [timeOffForm, setTimeOffForm] = useState({
    timeOffId: null,
    timeOffTypeId: "",
    startDate: "",
    endDate: "",
    reason: "",
    employee_id: '',
    startHalfDay: false,
    endHalfDay: false,
  });

  const [exportForm, setExportForm] = useState({
    startDate: "",
    endDate: "",
    employeeId: "",
    projectId: "",
    includeMilestones: true,
    includeActivityTypes: true,
    groupBy: "", // empty = no grouping, "company" = client company, "contact" = client name
  });
  
  // Canvas layout constants
  const EMPLOYEE_COL_WIDTH = 200;
  const DAY_COL_MIN_WIDTH = 30;
  const HEADER_HEIGHT = 60;
  const ROW_HEIGHT = 60;
  const BOOKING_HEIGHT = 50;
  const RESOURCE_ROW_HEIGHT = 13;
  const BOOKING_ROW_SLOT_HEIGHT = BOOKING_HEIGHT + RESOURCE_ROW_HEIGHT + 4;

  const visibleDates = getDates(currentWeekStart, daysToShow);

  // Calculate day column width
  const dayColWidth = Math.max(
    DAY_COL_MIN_WIDTH,
    (dimensions.width - EMPLOYEE_COL_WIDTH) / daysToShow
  );

  const employeeItemRowsBase = useMemo(() => {
    const perEmployeeItems = new Map();
    employees.forEach((employee) => {
      perEmployeeItems.set(employee.id.toString(), []);
    });

    bookings.forEach((booking) => {
      const key = booking.employee_id?.toString();
      if (!key || !perEmployeeItems.has(key)) return;
      perEmployeeItems.get(key).push({
        ...booking,
        type: 'booking',
        start_datetime: booking.start_datetime,
        end_datetime: booking.end_datetime,
      });
    });

    timeOffRequests.forEach((timeOff) => {
      const key = timeOff.employee_id?.toString();
      if (!key || !perEmployeeItems.has(key)) return;
      const startDateStr = toLocalISODate(new Date(timeOff.start_date));
      const endDateStr = toLocalISODate(new Date(timeOff.end_date));
      perEmployeeItems.get(key).push({
        ...timeOff,
        type: 'timeoff',
        start_datetime: `${startDateStr}T00:00:00`,
        end_datetime: `${endDateStr}T23:59:59`,
      });
    });

    const rowsByEmployee = new Map();
    perEmployeeItems.forEach((items, employeeId) => {
      rowsByEmployee.set(employeeId, assignItemsToRows(sortItemsByDurationDesc(items)));
    });

    return { perEmployeeItems, rowsByEmployee };
  }, [employees, bookings, timeOffRequests]);

  const employeeItemRowsRender = useMemo(() => {
    const rowsByEmployee = new Map(employeeItemRowsBase.rowsByEmployee);

    if (!isDragging || !draggedBooking) {
      return rowsByEmployee;
    }

    const draggedEmployeeId = draggedBooking.employee_id?.toString();
    if (!draggedEmployeeId || !employeeItemRowsBase.perEmployeeItems.has(draggedEmployeeId)) {
      return rowsByEmployee;
    }

    const baseItems = employeeItemRowsBase.perEmployeeItems
      .get(draggedEmployeeId)
      .filter((item) => !(item.type === 'booking' && item.id === draggedBooking.id));

    const bookingLeftX = currentMouseX - dragOffsetX;
    const dayIndex = Math.floor((bookingLeftX - EMPLOYEE_COL_WIDTH) / dayColWidth);
    if (dayIndex >= 0 && dayIndex < daysToShow) {
      const originalStart = new Date(draggedBooking.start_datetime);
      const originalEnd = new Date(draggedBooking.end_datetime);
      const durationDays = Math.max(1, Math.floor((originalEnd - originalStart) / (1000 * 60 * 60 * 24)) + 1);
      const baseDate = visibleDates[dayIndex];
      const newStartDate = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), 12, 0, 0, 0);
      const newEndDate = new Date(newStartDate.getTime() + (durationDays - 1) * 24 * 60 * 60 * 1000);
      newEndDate.setHours(12, 0, 0, 0);

      baseItems.push({
        ...draggedBooking,
        type: 'booking',
        start_datetime: newStartDate.toISOString(),
        end_datetime: newEndDate.toISOString(),
      });
    }

    rowsByEmployee.set(draggedEmployeeId, assignItemsToRows(sortItemsByDurationDesc(baseItems)));
    return rowsByEmployee;
  }, [employeeItemRowsBase, isDragging, draggedBooking, currentMouseX, dragOffsetX, EMPLOYEE_COL_WIDTH, dayColWidth, daysToShow, visibleDates]);

  // Calculate dynamic row heights for each employee based on their booking stack
  const employeeRowHeights = useMemo(() => {
    const heights = {};
    employees.forEach((employee) => {
      const rows = employeeItemRowsRender.get(employee.id.toString()) || [];
      const itemStackHeight = 5 + rows.length * BOOKING_ROW_SLOT_HEIGHT + 15;
      heights[employee.id] = Math.max(ROW_HEIGHT, itemStackHeight);
    });
    return heights;
  }, [employees, employeeItemRowsRender, ROW_HEIGHT, BOOKING_ROW_SLOT_HEIGHT]);

  const employeeYById = useMemo(() => {
    const yMap = {};
    let currentY = HEADER_HEIGHT;
    employees.forEach((employee) => {
      yMap[employee.id] = currentY;
      currentY += employeeRowHeights[employee.id] || ROW_HEIGHT;
    });
    return yMap;
  }, [employees, employeeRowHeights, HEADER_HEIGHT, ROW_HEIGHT]);

  const employeeHitAreas = useMemo(() => {
    const hitMap = new Map();

    employees.forEach((employee) => {
      const employeeId = employee.id.toString();
      const employeeY = employeeYById[employee.id] ?? HEADER_HEIGHT;
      const rows = employeeItemRowsRender.get(employeeId) || [];
      const areas = [];

      rows.forEach((rowItems, rowIdx) => {
        rowItems.forEach((item) => {
          const visibleSpan = getVisibleItemSpan(item.start_datetime, item.end_datetime, visibleDates);
          if (!visibleSpan) return;
          const { startDayIndex, spanDays } = visibleSpan;

          const x = EMPLOYEE_COL_WIDTH + startDayIndex * dayColWidth;
          const y = employeeY + 5 + rowIdx * BOOKING_ROW_SLOT_HEIGHT;
          const width = dayColWidth * spanDays;
          const height = item.type === 'booking' && item.resource_id ? BOOKING_HEIGHT + RESOURCE_ROW_HEIGHT + 2 : BOOKING_HEIGHT;

          areas.push({ item, rowIdx, x, y, width, height });
        });
      });

      hitMap.set(employeeId, areas);
    });

    return hitMap;
  }, [employees, employeeYById, employeeItemRowsRender, visibleDates, EMPLOYEE_COL_WIDTH, dayColWidth, BOOKING_ROW_SLOT_HEIGHT, BOOKING_HEIGHT, RESOURCE_ROW_HEIGHT, HEADER_HEIGHT]);

  // Helper function to find employee index and relative Y position from absolute Y coordinate
  const getEmployeeAtY = useCallback((absoluteY) => {
    if (absoluteY < HEADER_HEIGHT) return { employeeIndex: -1, relativeY: 0 };

    let currentY = HEADER_HEIGHT;
    for (let i = 0; i < employees.length; i++) {
      const employeeHeight = employeeRowHeights[employees[i].id] || ROW_HEIGHT;
      if (absoluteY >= currentY && absoluteY < currentY + employeeHeight) {
        return { employeeIndex: i, relativeY: absoluteY - currentY };
      }
      currentY += employeeHeight;
    }
    return { employeeIndex: -1, relativeY: 0 };
  }, [employees, employeeRowHeights, HEADER_HEIGHT, ROW_HEIGHT]);

  // Helper function to get employee Y position
  const getEmployeeY = useCallback((employeeIndex) => {
    const employee = employees[employeeIndex];
    if (!employee) return HEADER_HEIGHT;
    return employeeYById[employee.id] ?? HEADER_HEIGHT;
  }, [employees, employeeYById, HEADER_HEIGHT]);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const openEmployeeDetailModal = useCallback(async (employee) => {
    if (!employee) return;

    setShowEmployeeDetailModal(true);
    setEmployeeDetailTab('overview');
    setSelectedEmployeeDetail(employee);
    setEmployeeAssignedProjects([]);
    setIsEmployeeDetailLoading(true);
    setIsEmployeeProjectsLoading(true);

    try {
      const [allEmployeesData, assignedProjectsData] = await Promise.all([
        getEmployees().catch((err) => {
          console.error('Failed to fetch employee details:', err);
          return null;
        }),
        getProjectsForEmployee(employee.id).catch((err) => {
          console.error('Failed to fetch assigned projects for employee:', err);
          return null;
        }),
      ]);

      const allEmployees = allEmployeesData?.employees || [];
      const employeeIds = [employee.id, employee.employee_id]
        .filter((id) => id !== undefined && id !== null)
        .map((id) => id.toString());
      const detailedEmployee = allEmployees.find((emp) =>
        emp.user_id !== undefined &&
        emp.user_id !== null &&
        employeeIds.includes(emp.user_id.toString())
      );

      if (detailedEmployee) {
        setSelectedEmployeeDetail((prev) => ({ ...prev, ...detailedEmployee }));
      }

      setEmployeeAssignedProjects(Array.isArray(assignedProjectsData) ? assignedProjectsData : []);
    } finally {
      setIsEmployeeDetailLoading(false);
      setIsEmployeeProjectsLoading(false);
    }
  }, []);

  const handleBookingSubmit = async (e) => {
    e.preventDefault();

    if (!bookingForm.employeeId) {
      showToast('Please select an employee', 'error');
      return;
    }

    // At least one of project or activity type must be selected
    if (!bookingForm.projectId && !bookingForm.activityTypeId) {
      showToast('Please select a project and/or activity', 'error');
      return;
    }

    const isOwnBooking = bookingForm.employeeId.toString() === currentUser.user_id.toString();

    if (bookingForm.bookingId) {
      if (!canEditBookings) {
        showToast('You do not have permission to edit bookings', 'error');
        return;
      }
    } else {
      if (!canCreateBookings) {
        showToast('You do not have permission to create bookings', 'error');
        return;
      }

      if (!isOwnBooking && !canEditBookings) {
        showToast('You do not have permission to create bookings for other employees', 'error');
        return;
      }
    }

    try {
      const payload = {
        employee_id: bookingForm.employeeId || null,
        resource_id: bookingForm.resourceId || null,
        project_id: bookingForm.projectId || null,
        milestone_id: bookingForm.milestoneId || null,
        activity_type_id: bookingForm.activityTypeId || null,
        from_date: bookingForm.startDate,
        to_date: bookingForm.endDate,
        hours_per_day: bookingForm.hoursPerDay,
        description: bookingForm.description,
        is_time_specific: bookingForm.isTimeSpecific,
        start_time: bookingForm.startTime,
        end_time: bookingForm.endTime,
      };

      if (bookingForm.bookingId) {
        await updateBooking(bookingForm.bookingId, payload);
        showToast('Booking updated successfully', 'success');
      } else {
        await createBooking(payload);
        showToast('Booking created successfully', 'success');
      }

      await fetchBookings();
      setShowBookingModal(false);
      setBookingForm({
        bookingId: null,
        employeeId: "",
        resourceId: "",
        startDate: "",
        endDate: "",
        projectId: "",
        projectName: "",
        projectCompanyName: "",
        milestoneId: "",
        hoursPerDay: 8,
        description: "",
        isTimeSpecific: false,
        startTime: "09:00",
        endTime: "17:00",
      });
    } catch (error) {
      console.error("Booking failed:", error);
      showToast(error.message || "Failed to save booking", "error");
    }
  };

  const handleExportSubmit = async () => {
    try {
      // Build query params for export
      const params = {
        start_date: exportForm.startDate,
        end_date: exportForm.endDate,
      };

      if (exportForm.employeeId) params.employee_id = exportForm.employeeId;
      if (exportForm.projectId) params.project_id = exportForm.projectId;
      if (exportForm.includeMilestones) params.include_milestones = 'true';
      if (exportForm.includeActivityTypes) params.include_activity_types = 'true';
      if (exportForm.groupBy) params.group_by = exportForm.groupBy;

      // Call the export service
      await exportSchedule(params);
      showToast('Schedule exported successfully', 'success');
      setShowExportModal(false);
    } catch (error) {
      console.error("Export failed:", error);
      showToast(error.message || "Failed to export schedule", "error");
    }
  };

  const handleCreateActivityType = async () => {
    if (!newActivityTypeForm.name.trim()) {
      showToast("Activity name is required.", "error");
      return;
    }

    try {
      const newActivityType = await createActivityType({
        name: newActivityTypeForm.name.trim(),
        description: newActivityTypeForm.description.trim(),
        color: newActivityTypeForm.color
      });
      
      // Refetch activity types to ensure we have the latest data
      const fetchActivityTypes = async () => {
        try {
          const types = await getActivityTypes();
          setActivityTypes(types);
        } catch (err) {
          console.error("Failed to fetch activities:", err);
          showToast(err.message || "Failed to fetch activities", "error");
        }
      };
      await fetchActivityTypes();
      
      // Set the selected value in the booking form
      setBookingForm(prev => ({ ...prev, activityTypeId: newActivityType.activity_type_id }));
      
      // Reset modal state
      setShowNewActivityTypeModal(false);
      setNewActivityTypeForm({
        name: "",
        description: "",
        color: "#3b82f6"
      });
      
      showToast("Activity created successfully.", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to create activity.", "error");
    }
  };

  const fetchBookings = useCallback(async () => {
      try {
        const start = toLocalISODate(currentWeekStart);
        const endDate = new Date(currentWeekStart);
        endDate.setDate(endDate.getDate() + daysToShow - 1);
        const end = toLocalISODate(endDate);
        const res = await getBookings(start, end);
        setBookings(res);
      } catch (err) {
        console.error("Failed to fetch bookings:", err);
        showToast(err.message || "Failed to fetch bookings", "error");
      }
  }, [currentWeekStart, daysToShow, showToast]);

  const fetchTimeOffRequests = useCallback(async () => {
    try {
      
      const start = toLocalISODate(currentWeekStart);
      const endDate = new Date(currentWeekStart);
      endDate.setDate(endDate.getDate() + daysToShow - 1);
      const end = toLocalISODate(endDate);
      
      // Get time off requests for the visible period
      // Filter for approved and pending requests
      const params = {
        start_date: start,
        end_date: end,
        status: 'approved,pending'
      };
      
      const res = await getTimeOffRequests(params);
      setTimeOffRequests(res);
    } catch (err) {
      console.error("Failed to fetch time off requests:", err);
      showToast(err.message || "Failed to fetch time off requests", "error");
    }
  }, [currentWeekStart, daysToShow, showToast]);

  useEffect(() => {
    if (hasAnyTimeOffPermission) {
      const fetchTimeOffTypes = async () => {
        try {
          const types = await getTimeOffTypes();
          setTimeOffTypes(types);
          
          // Set the first time off type as default if available
          if (types.length > 0) {
            const firstTypeId = String(types[0].leave_type_id || types[0].id || 'unknown');
            setTimeOffForm(prev => ({ ...prev, timeOffTypeId: firstTypeId }));
          }
        } catch (err) {
          console.error("Failed to fetch time off types:", err);
          showToast(err.message || "Failed to fetch time off types", "error");
        }
      };
      fetchTimeOffTypes();
    }
  }, [hasAnyTimeOffPermission, showToast]);

  // Fetch activity types on mount
  useEffect(() => {
    const fetchActivityTypes = async () => {
      try {
        const types = await getActivityTypes();
        setActivityTypes(types);
      } catch (err) {
        console.error("Failed to fetch activities:", err);
        showToast(err.message || "Failed to fetch activities", "error");
      }
    };
    fetchActivityTypes();
  }, [showToast]);

  // Fetch Projects and Milestones when employee or project changes
  useEffect(() => {
    const fetchProjectsAndMilestones = async () => {
      if (!bookingForm.employeeId) {
        setEmployeeMilestones([]);
        setAllProjects([]);
        return;
      }

      try {
        // Fetch both all projects and employee's assigned projects
        const [allProjectsData, assignedProjectsData] = await Promise.all([
          getProjects(),
          getProjectsForEmployee(bookingForm.employeeId)
        ]);
        
        const allProjectsList = allProjectsData?.projects || allProjectsData || [];
        const assignedProjectsList = assignedProjectsData || [];
        
        // Store assigned project IDs for quick lookup
        const assignedProjectIds = new Set(assignedProjectsList.map(p => p.project_id?.toString()));
        
        // Mark projects as assigned or not
        const projectsWithAssignment = allProjectsList.map(project => ({
          ...project,
          isAssigned: assignedProjectIds.has(project.project_id?.toString())
        }));
        
        setAllProjects(projectsWithAssignment);

        // Fetch milestones if a project is selected
        if (bookingForm.projectId) {
          // Check if employee is assigned to this project
          const isAssignedToProject = assignedProjectIds.has(bookingForm.projectId?.toString());
          if (isAssignedToProject) {
            const milestones = await getAssignedMilestones(bookingForm.projectId, bookingForm.employeeId);
            setEmployeeMilestones(milestones || []);
          } else {
            // For unassigned projects, get all milestones
            const milestones = await getMilestones(bookingForm.projectId);
            setEmployeeMilestones(milestones || []);
          }
        } else {
          setEmployeeMilestones([]);
        }
      } catch (err) {
        console.error("Could not fetch employee's projects or milestones", err);
        showToast(err.message || "Failed to fetch employee projects", "error");
        setEmployeeMilestones([]);
        setAllProjects([]);
      }
    };

    fetchProjectsAndMilestones();
  }, [bookingForm.employeeId, bookingForm.projectId, allProjects.length, showToast]);

  // Sync companyFilter when booking modal opens or selected project changes externally (edit/prefill)
  useEffect(() => {
    if (!showBookingModal) {
      setCompanyFilter("");
      return;
    }
    if (bookingForm.projectId) {
      setCompanyFilter(bookingForm.projectCompanyName || "__none__");
    }
  }, [showBookingModal, bookingForm.projectId, bookingForm.projectCompanyName]);

  // Unique company options derived from assigned projects
  const bookingCompanyOptions = useMemo(() => {
    const names = new Set();
    let hasNone = false;
    (allProjects || []).filter((p) => p.isAssigned).forEach((p) => {
      if (p.client_company) names.add(p.client_company);
      else hasNone = true;
    });
    const opts = [{ value: "", label: "All companies" }];
    Array.from(names).sort().forEach((name) => opts.push({ value: name, label: name }));
    if (hasNone) opts.push({ value: "__none__", label: "No company" });
    return opts;
  }, [allProjects]);

  const filteredAssignedProjects = useMemo(() => {
    const assigned = (allProjects || []).filter((p) => p.isAssigned);
    if (!companyFilter) return assigned;
    if (companyFilter === "__none__") return assigned.filter((p) => !p.client_company);
    return assigned.filter((p) => p.client_company === companyFilter);
  }, [allProjects, companyFilter]);

  const fetchScheduleRows = useCallback(async () => {
    if (!canViewAllSchedules && !canViewOwnSchedule) return;

    try {
      setLoading(true);
      const data = await getScheduleEmployees();
      const employeeData = data.employees || data || [];
      const resourceData = data.resources || [];

      const mappedEmployees = employeeData.map((u) => ({
        id: u.user_id || u.employee_id,
        employee_id: u.employee_id,
        name: u.name,
        avatar_url: u.avatar_url,
        job_title: u.job_title,
        status: u.status,
        isResource: false
      }));

      const mappedResources = resourceData.map((r) => ({
        resource_id: r.resource_id,
        name: r.name,
        label: r.type === 'vehicle' ? `🚗 ${r.name}` : r.type === 'tool' ? `🔧 ${r.name}` : `📦 ${r.name}`,
        resource_type: r.type,
        availability_status: r.availability_status
      }));

      setEmployees(mappedEmployees);
      setResources(mappedResources);
    } catch (error) {
      console.error("Error fetching employees:", error);
      showToast(error.message || "Failed to fetch employees", "error");
    } finally {
      setLoading(false);
    }
  }, [canViewAllSchedules, canViewOwnSchedule, showToast]);

  const fetchScheduleVisibilityEmployees = useCallback(async () => {
    try {
      setIsVisibilityLoading(true);
      const data = await getScheduleEmployees({ include_hidden: true });
      const employeeData = (data.employees || data || []).map((employee) => ({
        employee_id: employee.employee_id,
        name: employee.name,
        job_title: employee.job_title,
        hide_on_schedule: !!employee.hide_on_schedule,
      }));
      setVisibilityEmployees(employeeData);
    } catch (error) {
      console.error("Error fetching schedule visibility employees:", error);
      showToast(error.message || "Failed to fetch employee visibility", "error");
    } finally {
      setIsVisibilityLoading(false);
    }
  }, [showToast]);

  const handleScheduleVisibilityToggle = async (employeeId, hidden) => {
    try {
      await updateScheduleEmployeeVisibility(employeeId, hidden);
      await Promise.all([fetchScheduleRows(), fetchScheduleVisibilityEmployees()]);
      showToast(`Employee ${hidden ? 'hidden from' : 'shown on'} schedule`, 'success');
    } catch (error) {
      console.error("Error updating schedule visibility:", error);
      showToast(error.message || "Failed to update schedule visibility", "error");
    }
  };

  // Fetch Employees and Resources
  useEffect(() => {
    fetchScheduleRows();
  }, [fetchScheduleRows]);

  // Load avatar images when employees change
  useEffect(() => {
    if (employees.length === 0) return;

    let cancelled = false;
    const MAX_CONCURRENT_LOADS = 6;

    const loadAvatars = async () => {
      const queue = employees.filter((employee) => {
        if (!employee.avatar_url) return false;
        const cached = avatarImages.get(employee.id);
        if (cached?.url === employee.avatar_url) return false;
        if (avatarLoadInFlightRef.current.has(employee.id)) return false;
        return true;
      });

      if (queue.length === 0) return;

      const loadedEntries = [];
      const workerCount = Math.min(MAX_CONCURRENT_LOADS, queue.length);

      const worker = async () => {
        while (!cancelled && queue.length > 0) {
          const employee = queue.shift();
          if (!employee) return;

          avatarLoadInFlightRef.current.add(employee.id);
          try {
            const img = await loadAvatarImage(employee.avatar_url, employee.id);
            if (img && !cancelled) {
              loadedEntries.push([employee.id, { url: employee.avatar_url, img }]);
            }
          } finally {
            avatarLoadInFlightRef.current.delete(employee.id);
          }
        }
      };

      await Promise.all(Array.from({ length: workerCount }, () => worker()));

      if (!cancelled && loadedEntries.length > 0) {
        setAvatarImages((prev) => {
          const updated = new Map(prev);
          let changed = false;
          loadedEntries.forEach(([id, entry]) => {
            const existing = updated.get(id);
            if (!existing || existing.url !== entry.url) {
              updated.set(id, entry);
              changed = true;
            }
          });
          return changed ? updated : prev;
        });
      }
    };

    loadAvatars();

    return () => { cancelled = true; };
  }, [employees, avatarImages]);

  // Fetch Bookings
  useEffect(() => {
    if (canViewAllSchedules || canViewOwnSchedule) {
      fetchBookings();
    }
  }, [fetchBookings, canViewAllSchedules, canViewOwnSchedule]);

  // Fetch Time Off Requests
  useEffect(() => {
    if (hasAnyTimeOffPermission) {
      fetchTimeOffRequests();
    }
  }, [fetchTimeOffRequests, hasAnyTimeOffPermission]);

  // Handle canvas resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: rect.width,
          height: window.innerHeight - rect.top,
        });
      }
    };

    updateDimensions();

    // When the loader unmounts and canvas container mounts, measure on next frame.
    if (!showLoading) {
      const rafId = window.requestAnimationFrame(updateDimensions);
      window.addEventListener('resize', updateDimensions);
      return () => {
        window.cancelAnimationFrame(rafId);
        window.removeEventListener('resize', updateDimensions);
      };
    }

    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [showLoading]);

  // Update export form dates when view changes
  useEffect(() => {
    const endDate = new Date(currentWeekStart);
    endDate.setDate(currentWeekStart.getDate() + daysToShow - 1);
    setExportForm(prev => ({
      ...prev,
      startDate: currentWeekStart.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    }));
  }, [currentWeekStart, daysToShow]);

  // Draw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || employees.length === 0) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    
    // Calculate total height based on individual employee row heights
    const totalEmployeeHeight = employees.reduce((sum, employee) => 
      sum + (employeeRowHeights[employee.id] || ROW_HEIGHT), 0);
    const totalHeight = Math.max(dimensions.height, HEADER_HEIGHT + totalEmployeeHeight);
    
    // Set canvas size accounting for device pixel ratio
    canvas.width = dimensions.width * dpr;
    canvas.height = totalHeight * dpr;
    canvas.style.width = `${dimensions.width}px`;
    canvas.style.height = `${totalHeight}px`;
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.clearRect(0, 0, dimensions.width, totalHeight);

    // Enable smooth rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Draw header background with subtle gradient
    const headerGradient = ctx.createLinearGradient(0, 0, 0, HEADER_HEIGHT);
    headerGradient.addColorStop(0, '#f8fafc');
    headerGradient.addColorStop(1, '#f1f5f9');
    ctx.fillStyle = headerGradient;
    ctx.fillRect(0, 0, dimensions.width, HEADER_HEIGHT);
    
    // Draw header border with subtle shadow
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.05)';
    ctx.shadowBlur = 2;
    ctx.shadowOffsetY = 1;
    ctx.beginPath();
    ctx.moveTo(0, HEADER_HEIGHT);
    ctx.lineTo(dimensions.width, HEADER_HEIGHT);
    ctx.stroke();
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    // Draw employee column header
    ctx.fillStyle = '#475569';
    ctx.font = '600 11px Inter, system-ui, sans-serif';
    ctx.textBaseline = 'middle';
    ctx.fillText('EMPLOYEE', 16, HEADER_HEIGHT / 2);

    // Collect all bookings and time off to draw after separators
    const allBookingsToDraw = [];
    const allTimeOffToDraw = [];

    // Draw employee rows
    let currentY = HEADER_HEIGHT;
    employees.forEach((employee, empIdx) => {
      const employeeHeight = employeeRowHeights[employee.id] || ROW_HEIGHT;
      const y = currentY;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, y, dimensions.width, employeeHeight);

      // Slightly stronger row separator for clearer employee boundaries
      ctx.strokeStyle = '#cbd5e1';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, y + employeeHeight);
      ctx.lineTo(dimensions.width, y + employeeHeight);
      ctx.stroke();

      // Employee column separator with improved styling
      ctx.strokeStyle = '#cbd5e1';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(EMPLOYEE_COL_WIDTH, 0);
      ctx.lineTo(EMPLOYEE_COL_WIDTH, totalHeight);
      ctx.stroke();

      // Avatar with improved styling (smaller)
      const avatarX = 16;
      const avatarY = y + employeeHeight / 2;
      const avatarRadius = 16;
      
      // Check if we have a loaded avatar image
      const avatarImg = avatarImages.get(employee.id)?.img;
      
      if (avatarImg) {
        // Draw actual avatar image
        ctx.save();
        ctx.beginPath();
        ctx.arc(avatarX + avatarRadius, avatarY, avatarRadius, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(avatarImg, avatarX, avatarY - avatarRadius, avatarRadius * 2, avatarRadius * 2);
        ctx.restore();
        
        // Avatar border
        ctx.strokeStyle = '#cbd5e1';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(avatarX + avatarRadius, avatarY, avatarRadius, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        // Draw placeholder avatar with initials
        const avatarGradient = ctx.createRadialGradient(
          avatarX + avatarRadius, avatarY - avatarRadius/2, 0,
          avatarX + avatarRadius, avatarY, avatarRadius
        );
        avatarGradient.addColorStop(0, '#f1f5f9');
        avatarGradient.addColorStop(1, '#e2e8f0');
        ctx.fillStyle = avatarGradient;
        ctx.beginPath();
        ctx.arc(avatarX + avatarRadius, avatarY, avatarRadius, 0, Math.PI * 2);
        ctx.fill();
        
        // Avatar border
        ctx.strokeStyle = '#cbd5e1';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        
        // Avatar text (smaller)
        ctx.fillStyle = '#334155';
        ctx.font = '600 10px Inter, system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(getInitials(employee.name), avatarX + avatarRadius, avatarY);
      }

      // Employee name and title with improved typography (smaller)
      ctx.fillStyle = '#1e293b';
      ctx.font = '500 11px Inter, system-ui, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(employee.name, avatarX + avatarRadius * 2 + 12, avatarY - 8);
      
      ctx.fillStyle = '#64748b';
      ctx.font = '400 10px Inter, system-ui, sans-serif';
      ctx.fillText(employee.job_title || '', avatarX + avatarRadius * 2 + 12, avatarY + 8);

      const employeeAreas = employeeHitAreas.get(employee.id.toString()) || [];
      employeeAreas.forEach(({ item, x: itemX, y: itemY, width: itemWidth }) => {
        if (item.type === 'booking') {
          const isDraggedPreview = !!(isDragging && draggedBooking && item.id === draggedBooking.id && item.employee_id?.toString() === draggedBooking.employee_id?.toString());
          allBookingsToDraw.push({
            booking: item,
            bookingX: itemX,
            bookingY: itemY,
            bookingWidth: itemWidth,
            isHovered: !isDraggedPreview && hoveredBooking?.id === item.id,
            isDraggedPreview,
          });
        } else if (item.type === 'timeoff') {
          allTimeOffToDraw.push({
            timeOff: item,
            timeOffX: itemX,
            timeOffY: itemY,
            timeOffWidth: itemWidth,
            isHovered: false,
          });
        }
      });
      currentY += employeeHeight;
    });

    // Draw day headers
    visibleDates.forEach((date, idx) => {
      const x = EMPLOYEE_COL_WIDTH + idx * dayColWidth;
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      
      // Weekend background - more subtle
      if (isWeekend) {
        ctx.fillStyle = 'rgba(241, 245, 249, 0.8)';
        ctx.fillRect(x, 0, dayColWidth, totalHeight);
      }

      // Date text with improved colors
      const dayNum = date.getDate().toString();
      const monthStr = date.toLocaleDateString('en-ZA', { month: 'short' });
      const weekdayStr = date.toLocaleDateString('en-ZA', { weekday: 'short' }).toUpperCase();
      
      ctx.textAlign = 'center';
      
      // Check if we have enough space for "15 Jan" format
      ctx.font = '600 10px Inter, system-ui, sans-serif';
      const fullDateText = `${dayNum} ${monthStr}`;
      const textWidth = ctx.measureText(fullDateText).width;
      
      if (dayColWidth >= textWidth + 16) { // Enough space for compact format
        // Show "15 Jan" and "MON" below
        ctx.fillStyle = isWeekend ? '#64748b' : '#1e293b';
        ctx.fillText(fullDateText, x + dayColWidth / 2, HEADER_HEIGHT / 2 - 8);
        
        ctx.fillStyle = isWeekend ? '#94a3b8' : '#64748b';
        ctx.font = '500 10px Inter, system-ui, sans-serif';
        ctx.fillText(weekdayStr, x + dayColWidth / 2, HEADER_HEIGHT / 2 + 12);
      } else { // Narrow columns - stack vertically
        // Show day number, month, and weekday stacked
        ctx.fillStyle = isWeekend ? '#64748b' : '#1e293b';
        ctx.font = '600 10px Inter, system-ui, sans-serif';
        ctx.fillText(dayNum, x + dayColWidth / 2, HEADER_HEIGHT / 2 - 12);
        
        ctx.font = '500 10px Inter, system-ui, sans-serif';
        ctx.fillText(monthStr, x + dayColWidth / 2, HEADER_HEIGHT / 2);
        
        ctx.fillStyle = isWeekend ? '#94a3b8' : '#64748b';
        ctx.fillText(weekdayStr, x + dayColWidth / 2, HEADER_HEIGHT / 2 + 12);
      }
      
      if(idx !== 0){
        if(weekdayStr === 'MON' || weekdayStr === 'SAT') {
          ctx.strokeStyle = '#a6a2d6ff';
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, totalHeight);
          ctx.stroke();
        }
        
        else{
          ctx.strokeStyle = '#e2e8f0';
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, totalHeight);
          ctx.stroke();
        }
      }
    });

    // Draw drag selection if active
    if (isDragging && dragStart) {
      const startDayIdx = dragEnd 
        ? Math.min(dragStart.dayIndex, dragEnd.dayIndex)
        : dragStart.dayIndex;
      const endDayIdx = dragEnd 
        ? Math.max(dragStart.dayIndex, dragEnd.dayIndex)
        : dragStart.dayIndex;
      
      const rowY = getEmployeeY(dragStart.employeeIndex);
      const employeeHeight = employeeRowHeights[employees[dragStart.employeeIndex].id] || ROW_HEIGHT;
      
      // Draw each selected cell individually with improved styling
      for (let i = startDayIdx; i <= endDayIdx; i++) {
        const cellX = EMPLOYEE_COL_WIDTH + i * dayColWidth;
        
        // Fill with subtle gradient
        const selectionGradient = ctx.createLinearGradient(cellX, rowY, cellX, rowY + employeeHeight);
        selectionGradient.addColorStop(0, 'rgba(59, 130, 246, 0.15)');
        selectionGradient.addColorStop(1, 'rgba(59, 130, 246, 0.08)');
        ctx.fillStyle = selectionGradient;
        ctx.fillRect(cellX + 1, rowY + 1, dayColWidth - 2, employeeHeight - 2);
        
        // Border with rounded corners
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.roundRect(cellX + 1, rowY + 1, dayColWidth - 2, employeeHeight - 2, 4);
        ctx.stroke();
      }
    }

    // Draw all bookings after separators
    allBookingsToDraw.forEach(({ booking, bookingX, bookingY, bookingWidth, isHovered, isDraggedPreview }) => {
      if (isDraggedPreview) {
        ctx.globalAlpha = 0.5;
      }
      drawBookingBlock(ctx, booking, bookingX, bookingY, bookingWidth, isHovered, BOOKING_HEIGHT, RESOURCE_ROW_HEIGHT);
      if (isDraggedPreview) {
        ctx.globalAlpha = 1.0;
      }
    });

    // Draw all time off blocks
    allTimeOffToDraw.forEach(({ timeOff, timeOffX, timeOffY, timeOffWidth, isHovered }) => {
      drawTimeOffBlock(ctx, timeOff, timeOffX, timeOffY, timeOffWidth, isHovered, BOOKING_HEIGHT);
    });

    // Draw free-following dragged booking
    if (isDragging && draggedBooking) {
      const durationDays = Math.max(1, Math.floor((new Date(draggedBooking.end_datetime) - new Date(draggedBooking.start_datetime)) / (1000 * 60 * 60 * 24)) + 1);
      const bookingWidth = durationDays * dayColWidth;
      const bookingX = Math.max(EMPLOYEE_COL_WIDTH, Math.min(currentMouseX - dragOffsetX, dimensions.width - bookingWidth));
      const draggedBookingHeight = draggedBooking?.resource_id ? BOOKING_HEIGHT + RESOURCE_ROW_HEIGHT + 2 : BOOKING_HEIGHT;
      const bookingY = Math.max(HEADER_HEIGHT, Math.min(currentMouseY - dragOffsetY, totalHeight - draggedBookingHeight));

      ctx.globalAlpha = 0.7;
      drawBookingBlock(ctx, draggedBooking, bookingX, bookingY, bookingWidth, false, BOOKING_HEIGHT, RESOURCE_ROW_HEIGHT);
      ctx.globalAlpha = 1.0;

    }

    // Copy the header region to the sticky overlay canvas so it remains visible while scrolling.
    const overlay = headerOverlayRef.current;
    if (overlay && canvas.width > 0 && canvas.height > 0) {
      overlay.width = dimensions.width * dpr;
      overlay.height = HEADER_HEIGHT * dpr;
      overlay.style.width = `${dimensions.width}px`;
      overlay.style.height = `${HEADER_HEIGHT}px`;
      const overlayCtx = overlay.getContext('2d');
      overlayCtx.setTransform(1, 0, 0, 1, 0, 0);
      overlayCtx.clearRect(0, 0, overlay.width, overlay.height);
      overlayCtx.drawImage(
        canvas,
        0, 0, dimensions.width * dpr, HEADER_HEIGHT * dpr,
        0, 0, dimensions.width * dpr, HEADER_HEIGHT * dpr
      );
    }

  }, [dimensions, employees, visibleDates, dayColWidth, hoveredBooking, isDragging, dragStart, dragEnd, draggedBooking, draggedBookingRowIdx, dragOffsetX, dragOffsetY, employeeRowHeights, getEmployeeY, currentMouseX, currentMouseY, avatarImages, daysToShow, BOOKING_HEIGHT, RESOURCE_ROW_HEIGHT, employeeHitAreas, EMPLOYEE_COL_WIDTH, HEADER_HEIGHT, ROW_HEIGHT]);

  // Mouse event handlers
  const handleMouseMove = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Handle dragging existing booking
    if (isDragging && draggedBooking) {
      setCurrentMouseX(x);
      setCurrentMouseY(y);
      return;
    }

    // Handle drag selection
    if (isDragging && dragStart) {
      if (y < HEADER_HEIGHT || x < EMPLOYEE_COL_WIDTH) {
        setDragEnd(null);
        return;
      }
      
      const { employeeIndex } = getEmployeeAtY(y);
      const dayIndex = Math.floor((x - EMPLOYEE_COL_WIDTH) / dayColWidth);

      // Only allow dragging within the same employee row
      if (employeeIndex === dragStart.employeeIndex && 
          dayIndex >= 0 && dayIndex < daysToShow) {
        setDragEnd({ employeeIndex, dayIndex });
      } else if (employeeIndex !== dragStart.employeeIndex) {
        // If moved to different row, reset to just the start cell
        setDragEnd(null);
      }
      return;
    }

    // Check if hovering over a booking (only when not dragging)
    if (y < HEADER_HEIGHT) {
      setHoveredBooking(null);
      return;
    }

    const { employeeIndex } = getEmployeeAtY(y);
    const dayIndex = Math.max(0, Math.floor((x - EMPLOYEE_COL_WIDTH) / dayColWidth));

    if (employeeIndex < 0 || employeeIndex >= employees.length || dayIndex < 0 || dayIndex >= daysToShow) {
      setHoveredBooking(null);
      return;
    }

    const employee = employees[employeeIndex];
    let foundBooking = null;

    const employeeAreas = employeeHitAreas.get(employee.id.toString()) || [];
    employeeAreas.forEach(({ item, x: areaX, y: areaY, width: areaWidth, height: areaHeight }) => {
      if (x >= areaX && x <= areaX + areaWidth && y >= areaY && y <= areaY + areaHeight) {
        foundBooking = item;
      }
    });

    setHoveredBooking(prev => {
      const prevId = prev?.id ?? null;
      const nextId = foundBooking?.id ?? null;
      const prevType = prev?.type ?? null;
      const nextType = foundBooking?.type ?? null;
      if (prevId === nextId && prevType === nextType) return prev;
      return foundBooking;
    });
  }, [employees, dayColWidth, daysToShow, isDragging, dragStart, draggedBooking, getEmployeeAtY, employeeHitAreas, HEADER_HEIGHT, EMPLOYEE_COL_WIDTH]);

  const handleMouseDown = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (y < HEADER_HEIGHT || x < EMPLOYEE_COL_WIDTH) return;

    const { employeeIndex } = getEmployeeAtY(y);
    const dayIndex = Math.floor((x - EMPLOYEE_COL_WIDTH) / dayColWidth);

    if (employeeIndex >= 0 && employeeIndex < employees.length && 
        dayIndex >= 0 && dayIndex < daysToShow) {
      
      const employee = employees[employeeIndex];
      
      let clickedItem = null;
      let itemOffsetX = 0;
      let itemOffsetY = 0;
      let clickedItemRowIdx = null;

      const employeeAreas = employeeHitAreas.get(employee.id.toString()) || [];
      employeeAreas.forEach(({ item, rowIdx, x: areaX, y: areaY, width: areaWidth, height: areaHeight }) => {
        if (x >= areaX && x <= areaX + areaWidth && y >= areaY && y <= areaY + areaHeight) {
          clickedItem = item;
          itemOffsetX = x - areaX;
          itemOffsetY = y - areaY;
          clickedItemRowIdx = rowIdx;
        }
      });
      
      if (clickedItem) {
        if (clickedItem.type === 'booking') {
          // Start dragging the booking
          const isOwnBooking = clickedItem.employee_id && clickedItem.employee_id.toString() === currentUser.user_id.toString();
          if (!isOwnBooking && !canEditBookings) {
            showToast('You do not have permission to move this booking', 'error');
            return;
          }
          
          setDraggedBooking(clickedItem);
          setDraggedBookingRowIdx(clickedItemRowIdx);
          setDragOffsetX(itemOffsetX);
          setDragOffsetY(itemOffsetY);
          setCurrentMouseX(x);
          setCurrentMouseY(y);
          setIsDragging(true);
          setWasDraggingBooking(true);
        } else if (clickedItem.type === 'timeoff') {
          // Handle time off click - could open edit modal or show info
          // For now, just show a toast indicating time off was clicked
          // showToast(`Time off: ${clickedItem.type_name} (${clickedItem.status})`, 'info');
        }
      } else {
        if (!canCreateBookings) {
          showToast('You do not have permission to create bookings', 'error');
          return;
        }
        // Start drag selection for new booking
        setIsDragging(true);
        setDragStart({ employeeIndex, dayIndex });
        setDragEnd(null);
        setWasDraggingBooking(false);
      }
    }
  }, [employees, dayColWidth, daysToShow, currentUser, canCreateBookings, canEditBookings, showToast, getEmployeeAtY, employeeHitAreas, HEADER_HEIGHT, EMPLOYEE_COL_WIDTH]);

  const handleMouseUp = useCallback(async () => {
    
    if (isDragging && draggedBooking) {
      // Calculate new position based on booking's left edge position
      const bookingLeftX = currentMouseX - dragOffsetX;
      const dayIndex = Math.floor((bookingLeftX - EMPLOYEE_COL_WIDTH) / dayColWidth);
      
      if (dayIndex >= 0 && dayIndex < daysToShow) {
        // Check if the booking actually moved (not just a click)
        const originalStart = new Date(draggedBooking.start_datetime);
        const originalDayIndex = visibleDates.findIndex(
          date => toLocalISODate(date) === toLocalISODate(originalStart)
        );
        
        if (dayIndex !== originalDayIndex) {
          // Actual drag occurred - process the move
        // Calculate original booking duration
        const originalStart = new Date(draggedBooking.start_datetime);
        const originalEnd = new Date(draggedBooking.end_datetime);
        const durationDays = Math.max(1, Math.floor((originalEnd - originalStart) / (1000 * 60 * 60 * 24)) + 1);
        
        // New start date
        const targetDate = visibleDates[dayIndex];
        const newStartDate = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 
                                     originalStart.getHours(), originalStart.getMinutes(), 0, 0);
        
        // New end date (maintain same duration)
        const newEndDate = new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate() + durationDays - 1,
                                   originalEnd.getHours(), originalEnd.getMinutes(), 0, 0);
        
        // Optimistically update the booking position
        const updatedBookings = bookings.map(b =>
          b.id === draggedBooking.id ? { ...b, start_datetime: newStartDate.toISOString(), end_datetime: newEndDate.toISOString() } : b
        );
        setBookings(updatedBookings);
        
        try {
          await updateBooking(draggedBooking.id, {
            start_datetime: newStartDate.toISOString(),
            end_datetime: newEndDate.toISOString(),
          });

          // Refresh bookings to confirm
          fetchBookings();
          showToast('Booking moved successfully', 'success');
          setJustDragged(true);
          setTimeout(() => setJustDragged(false), 100);
        } catch (error) {
          console.error('Error updating booking:', error);
          // Revert optimistic update on error
          setBookings(bookings);
          showToast('Failed to move booking', 'error');
        }
        } // Close the movement check
      }
    } else if (isDragging && dragStart && !wasDraggingBooking) {
      const startDay = Math.min(dragStart.dayIndex, dragEnd?.dayIndex ?? dragStart.dayIndex);
      const endDay = Math.max(dragStart.dayIndex, dragEnd?.dayIndex ?? dragStart.dayIndex);
      
      const selectedStartDate = toLocalISODate(visibleDates[startDay]);
      const selectedEndDate = toLocalISODate(visibleDates[endDay]);
      
      const targetRow = employees[dragStart.employeeIndex];
      
      setBookingForm({
        bookingId: null,
        employeeId: targetRow.id,
        resourceId: "",
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        projectId: "",
        projectName: "",
        projectCompanyName: "",
        milestoneId: "",
        activityTypeId: "",
        hoursPerDay: 8,
        description: "",
        isTimeSpecific: false,
        startTime: "09:00",
        endTime: "17:00",
      });

      setTimeOffForm({
        employee_id: targetRow.id,
        timeOffTypeId: timeOffTypes.length > 0 ? timeOffTypes[0].leave_type_id : "",
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        reason: "",
      });
      
      setShowBookingModal(true);
      setJustDragged(true);
      setTimeout(() => setJustDragged(false), 100);
    }
    
    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
    setDraggedBooking(null);
    setDraggedBookingRowIdx(null);
    setDragOffsetX(0);
    setDragOffsetY(0);
    setCurrentMouseX(0);
    setCurrentMouseY(0);
    setWasDraggingBooking(false);
  }, [isDragging, draggedBooking, currentMouseX, dragOffsetX, dragStart, dragEnd, wasDraggingBooking, visibleDates, dayColWidth, EMPLOYEE_COL_WIDTH, daysToShow, fetchBookings, showToast, bookings, employees, timeOffTypes]);

  const handleClick = useCallback((e) => {
    if (justDragged || wasDraggingBooking) return;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (y >= HEADER_HEIGHT && x >= 0 && x < EMPLOYEE_COL_WIDTH) {
      const { employeeIndex } = getEmployeeAtY(y);
      if (employeeIndex >= 0 && employeeIndex < employees.length) {
        openEmployeeDetailModal(employees[employeeIndex]);
      }
      return;
    }

    if (hoveredBooking) {
      if (hoveredBooking.type === 'booking') {
        // Permission check for editing
        const isOwnBooking = hoveredBooking.employee_id && hoveredBooking.employee_id.toString() === currentUser.user_id.toString();
        if (!isOwnBooking && !canEditBookings) {
          showToast('You do not have permission to edit this booking', 'error');
          return;
        }

        setBookingForm({
          bookingId: hoveredBooking.id,
          employeeId: hoveredBooking.employee_id || "",
          resourceId: hoveredBooking.resource_id || "",
          startDate: toLocalISODate(new Date(hoveredBooking.start_datetime)),
          endDate: toLocalISODate(new Date(hoveredBooking.end_datetime)),
          projectId: hoveredBooking.project_id,
          projectName: hoveredBooking.project_name || "",
          projectCompanyName: hoveredBooking.client_company || "",
          milestoneId: hoveredBooking.milestone_id || "",
          activityTypeId: hoveredBooking.activity_type_id || "",
          hoursPerDay: Number(hoveredBooking.hours_allocated/(Math.floor((new Date(hoveredBooking.end_datetime) - new Date(hoveredBooking.start_datetime)) / (1000 * 60 * 60 * 24)) + 1)) || 8,
          description: hoveredBooking.notes || "",
          isTimeSpecific: hoveredBooking.is_time_specific,
          startTime: hoveredBooking.start_time || "09:00",
          endTime: hoveredBooking.end_time || "17:00",
        });
      setShowBookingModal(true);
      } else if (hoveredBooking.type === 'timeoff') {
        // Handle time off click - check if pending and can approve
        const isPending = hoveredBooking.status === 'pending';
        if (isPending && canApproveTimeOff) {
          setSelectedTimeOffForApproval(hoveredBooking);
          setShowTimeOffApprovalModal(true);
        } else {
          // Handle time off edit
          const isOwnRequest = hoveredBooking.employee_id.toString() === currentUser.user_id.toString();
          const canEditThisTimeOff = 
            (isOwnRequest && canEditOwnPendingTimeOff && isPending) ||
            (canApproveTimeOff) || // Managers can edit any time-off
            (canEditApprovedTimeOff && hoveredBooking.status === 'approved');
          
          if (!canEditThisTimeOff) {
            showToast('You do not have permission to edit this time off request', 'error');
            return;
          }

          setTimeOffForm({
            employee_id: hoveredBooking.employee_id,
            timeOffId: hoveredBooking.leave_request_id,
            timeOffTypeId: hoveredBooking.time_off_type_id,
            startDate: toLocalISODate(new Date(hoveredBooking.start_date)),
            endDate: toLocalISODate(new Date(hoveredBooking.end_date)),
            reason: hoveredBooking.reason || "",
            startHalfDay: !!hoveredBooking.start_half_day,
            endHalfDay: !!hoveredBooking.end_half_day,
          });
          setShowTimeOffModal(true);
        }
      }
    }
  }, [justDragged, wasDraggingBooking, HEADER_HEIGHT, EMPLOYEE_COL_WIDTH, getEmployeeAtY, employees, openEmployeeDetailModal, hoveredBooking, currentUser, canEditBookings, canEditOwnPendingTimeOff, canEditApprovedTimeOff, showToast, canApproveTimeOff]);

  // Navigation handlers
  const handleNext = () => {
    const next = new Date(currentWeekStart);
    next.setDate(currentWeekStart.getDate() + daysToShow);
    setCurrentWeekStart(getMonday(next));
  };

  const handlePrev = () => {
    const prev = new Date(currentWeekStart);
    prev.setDate(currentWeekStart.getDate() - daysToShow);
    setCurrentWeekStart(getMonday(prev));
  };

  const formatDateRange = () => {
    const endDate = new Date(currentWeekStart);
    endDate.setDate(currentWeekStart.getDate() + daysToShow - 1);
    const startMonth = currentWeekStart.toLocaleDateString('en-US', { month: 'short' });
    const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' });
    const startDay = currentWeekStart.getDate();
    const endDay = endDate.getDate();
    const year = endDate.getFullYear();
    
    if (startMonth === endMonth) {
      return `${startMonth} ${startDay} - ${endDay}, ${year}`;
    }
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
  };

  const handleTimeOffSubmit = async (e) => {
    e.preventDefault();

    if (!timeOffForm.timeOffTypeId || !timeOffForm.startDate || !timeOffForm.endDate) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    try {
      if (timeOffForm.timeOffId) {
        await updateTimeOffRequest(timeOffForm.timeOffId, buildTimeOffUpdatePayload(timeOffForm));
        showToast('Time off request updated successfully', 'success');
      } else {
        await createTimeOffRequest(buildTimeOffCreatePayload(timeOffForm, currentUser.user_id));
        showToast('Time off request submitted successfully', 'success');
      }

      // Reset form and close modal
      await fetchTimeOffRequests();
      refreshPendingCount();
      setShowBookingModal(false);
      setShowTimeOffModal(false);
      setActiveTab("booking");
      setTimeOffForm({
        employee_id: '',
        timeOffTypeId: "",
        startDate: "",
        endDate: "",
        reason: "",
        startHalfDay: false,
        endHalfDay: false,
      });
    } catch (error) {
      console.error("Time off request failed:", error);
      showToast(error.response?.data?.error || error.message || "Failed to submit time off request", "error");
    }
  };

  // Shared time-off date + half-day inputs, used by both time-off form modals.
  const renderTimeOffDatesField = () => {
    const sameDay = timeOffForm.startDate && timeOffForm.startDate === timeOffForm.endDate;
    const days = countLeaveDays(timeOffForm.startDate, timeOffForm.endDate, timeOffForm.startHalfDay, timeOffForm.endHalfDay);
    return (
      <div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Start Date</label>
            <input
              type="date"
              value={timeOffForm.startDate}
              onChange={(e) => setTimeOffForm((prev) => ({ ...prev, startDate: e.target.value }))}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>End Date</label>
            <input
              type="date"
              value={timeOffForm.endDate}
              onChange={(e) => setTimeOffForm((prev) => ({ ...prev, endDate: e.target.value }))}
              style={{ width: '100%' }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '0.85rem', color: '#374151' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input
              type="checkbox"
              checked={!!timeOffForm.startHalfDay}
              onChange={(e) => setTimeOffForm((prev) => ({ ...prev, startHalfDay: e.target.checked }))}
            />
            {sameDay ? 'Half day' : 'First day is a half-day'}
          </label>
          {!sameDay && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="checkbox"
                checked={!!timeOffForm.endHalfDay}
                onChange={(e) => setTimeOffForm((prev) => ({ ...prev, endHalfDay: e.target.checked }))}
              />
              Last day is a half-day
            </label>
          )}
        </div>
        {timeOffForm.startDate && timeOffForm.endDate && (
          <div style={{ marginTop: '6px', fontSize: '0.8rem', color: '#6b7280' }}>
            {formatDays(days)} working day{days === 1 ? '' : 's'} (weekends excluded)
          </div>
        )}
      </div>
    );
  };

  const handleApproveTimeOff = async (timeOffId) => {
    try {
      await updateTimeOffRequest(timeOffId, { status: 'approved' });
      showToast('Time off request approved', 'success');
      fetchTimeOffRequests();
      await fetchPendingTimeOffRequests(); // Refresh pending requests
      refreshPendingCount();
      setShowTimeOffApprovalModal(false);
      setShowNotificationsModal(false);
    } catch (error) {
      showToast('Failed to approve time off', 'error');
    }
  };

  const handleDenyTimeOff = async (timeOffId) => {
    try {
      await updateTimeOffRequest(timeOffId, { status: 'rejected' });
      showToast('Time off request rejected', 'success');
      fetchTimeOffRequests();
      await fetchPendingTimeOffRequests(); // Refresh pending requests
      refreshPendingCount();
      setShowTimeOffApprovalModal(false);
      setShowNotificationsModal(false);
    } catch (error) {
      showToast('Failed to reject time off', 'error');
    }
  };

  const handleEditTimeOffApproval = () => {
    setTimeOffForm({
      employee_id: selectedTimeOffForApproval.employee_id,
      timeOffId: selectedTimeOffForApproval.leave_request_id,
      timeOffTypeId: selectedTimeOffForApproval.time_off_type_id,
      startDate: toLocalISODate(new Date(selectedTimeOffForApproval.start_date)),
      endDate: toLocalISODate(new Date(selectedTimeOffForApproval.end_date)),
      reason: selectedTimeOffForApproval.reason || "",
      startHalfDay: !!selectedTimeOffForApproval.start_half_day,
      endHalfDay: !!selectedTimeOffForApproval.end_half_day,
    });
    setActiveTab("timeoff");
    setShowBookingModal(true);
    setShowTimeOffApprovalModal(false);
  };

  const handleEditTimeOffFromNotification = (request) => {
    setTimeOffForm({
      employee_id: request.employee_id,
      timeOffId: request.leave_request_id,
      timeOffTypeId: request.time_off_type_id,
      startDate: toLocalISODate(new Date(request.start_date)),
      endDate: toLocalISODate(new Date(request.end_date)),
      reason: request.reason || "",
      startHalfDay: !!request.start_half_day,
      endHalfDay: !!request.end_half_day,
    });
    setActiveTab("timeoff");
    setShowBookingModal(true);
    setShowNotificationsModal(false);
  };

  const fetchPendingTimeOffRequests = useCallback(async () => {
    if (!canApproveTimeOff) return;

    try {
      const response = await getTimeOffRequests({ status: 'pending' });
      const sortedRequests = response.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      setPendingTimeOffRequests(sortedRequests);
    } catch (error) {
      console.error('Error fetching pending time off requests:', error);
    }
  }, [canApproveTimeOff]);

  const fetchScheduleNotificationCount = useCallback(async () => {
    try {
      const counts = await getNotificationCounts();
      setPendingNotificationsCount(counts.schedule || 0);
    } catch (error) {
      // Keep schedule usable even if notifications API fails.
    }
  }, []);

  const handleNotificationsClick = async () => {
    if (canApproveTimeOff) {
      await fetchPendingTimeOffRequests();
    }
    setShowNotificationsModal(true);
  };

  useEffect(() => {
    let isMounted = true;

    const loadCounts = async () => {
      if (!isMounted) return;
      await fetchScheduleNotificationCount();
    };

    loadCounts();
    const intervalId = setInterval(loadCounts, 30000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [fetchScheduleNotificationCount]);

  const isViewingOwnScheduleOnly = !canViewAllSchedules && canViewOwnSchedule;

  return (
    <div className={styles.scheduleWrapper}>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {showLoading ? (
        <div className={styles.scheduleSkeleton}>
          <div className={styles.scheduleSkeletonHeader}>
            <div className={styles.scheduleSkeletonLeft}>
              <div className={styles.scheduleSkeletonTitle} />
            </div>
            <div className={styles.scheduleSkeletonCenter}>
              <div className={styles.scheduleSkeletonIconBtn} />
              <div className={styles.scheduleSkeletonDateBadge} />
              <div className={styles.scheduleSkeletonIconBtn} />
            </div>
            <div className={styles.scheduleSkeletonRight}>
              <div className={styles.scheduleSkeletonToggleGroup}>
                <div className={styles.scheduleSkeletonTogglePill} />
                <div className={styles.scheduleSkeletonTogglePill} />
                <div className={styles.scheduleSkeletonTogglePill} />
              </div>
              <div className={styles.scheduleSkeletonActionBtn} />
              <div className={styles.scheduleSkeletonActionBtn} />
              <div className={styles.scheduleSkeletonActionBtn} />
            </div>
          </div>

          <div className={styles.scheduleSkeletonBoard}>
            <div className={styles.scheduleSkeletonBoardHeader}>
              <div className={styles.scheduleSkeletonNameHeader} />
              {Array.from({ length: 7 }).map((_, idx) => (
                <div key={`schedule-day-header-${idx}`} className={styles.scheduleSkeletonDayHeader} />
              ))}
            </div>
            {Array.from({ length: 7 }).map((_, rowIdx) => (
              <div key={`schedule-row-${rowIdx}`} className={styles.scheduleSkeletonRow}>
                <div className={styles.scheduleSkeletonNameCell}>
                  <div className={styles.scheduleSkeletonAvatar} />
                  <div className={styles.scheduleSkeletonNameStack}>
                    <div className={styles.scheduleSkeletonNameLine} />
                    <div className={styles.scheduleSkeletonRoleLine} />
                  </div>
                </div>
                {Array.from({ length: 7 }).map((_, colIdx) => (
                  <div key={`schedule-cell-${rowIdx}-${colIdx}`} className={styles.scheduleSkeletonDayCell} />
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>

      {/* Header */}
      <div className={`${styles.header} ${ui.fadeInContent}`}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>
            {isViewingOwnScheduleOnly ? 'My Schedule' : 'Team Schedule'}
          </h2>
        </div>
        <div className={styles.headerCenter}>
          <button className={styles.navButton} onClick={handlePrev} title="Previous">
            ←
          </button>
          <span className={styles.weekLabel}>{formatDateRange()}</span>
          <button className={styles.navButton} onClick={handleNext} title="Next">
            →
          </button>
        </div>
        <div className={styles.headerRight}>
          <button 
            className={styles.notificationsButton} 
            onClick={handleNotificationsClick}
            title="Notifications"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C10.896 2 10 2.896 10 4V5.586L8.707 6.879C8.077 7.509 7.705 8.358 7.705 9.243V15H6C5.448 15 5 15.448 5 16C5 16.552 5.448 17 6 17H18C18.552 17 19 16.552 19 16C19 15.448 18.552 15 18 15H16.295V9.243C16.295 8.358 15.923 7.509 15.293 6.879L14 5.586V4C14 2.896 13.104 2 12 2Z" fill="currentColor"/>
              <path d="M10 18C10 19.105 10.895 20 12 20C13.105 20 14 19.105 14 18H10Z" fill="currentColor"/>
            </svg>
            {pendingNotificationsCount > 0 && (
              <span className={styles.notificationBadge}>
                {pendingNotificationsCount > 99 ? '99+' : pendingNotificationsCount}
              </span>
            )}
          </button>
          <div className={styles.daySelector}>
            {DAY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`${styles.daySelectorBtn} ${daysToShow === opt.value ? styles.daySelectorActive : ''}`}
                onClick={() => setDaysToShow(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button 
            className={styles.todayButton} 
            onClick={() => setCurrentWeekStart(getMonday(new Date()))}
          >
            Today
          </button>
          {canViewAllSchedules && canEditBookings && (
            <button
              className={styles.todayButton}
              onClick={async () => {
                setShowVisibilityModal(true);
                await fetchScheduleVisibilityEmployees();
              }}
              title="Manage employee visibility"
            >
              Employees
            </button>
          )}
          {canApproveTimeOff && pendingCount > 0 && (
            <button
              className={styles.todayButton}
              onClick={handleNotificationsClick}
              title="Pending time off requests"
            >
              Pending ({pendingCount})
            </button>
          )}
          <button
            className={styles.todayButton}
            onClick={() => setShowExportModal(true)}
            title="Export to CSV"
          >
            Export
          </button>
        </div>
      </div>

      {/* Canvas Container */}
      <div
        className={ui.fadeInContent}
        ref={containerRef}
        style={{
          flex: 1,
          overflow: 'auto',
          position: 'relative',
        }}
      >
        <canvas
          ref={headerOverlayRef}
          style={{
            position: 'sticky',
            top: 0,
            left: 0,
            zIndex: 10,
            display: 'block',
            pointerEvents: 'none',
          }}
        />
        <canvas
          ref={canvasRef}
          onMouseMove={handleMouseMove}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onClick={handleClick}
          onMouseLeave={() => {
            if (isDragging) {
              setIsDragging(false);
              setDragStart(null);
              setDragEnd(null);
              setDraggedBooking(null);
              setDraggedBookingRowIdx(null);
              setDragOffsetX(0);
              setDragOffsetY(0);
              setCurrentMouseX(0);
              setWasDraggingBooking(false);
            }
          }}
          style={{
            display: 'block',
            cursor: hoveredBooking ? 'pointer' : isDragging ? 'grabbing' : 'pointer',
            marginTop: `-${HEADER_HEIGHT}px`,
          }}
        />
      </div>

        </>
      )}

      {/* Hover Tooltip
      {hoveredBooking && hoveredBooking.type === 'booking' && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          background: '#1f2937',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          fontSize: '0.875rem',
          maxWidth: '300px',
          zIndex: 1000,
        }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>
            {hoveredBooking.project_name}
          </div>
          {hoveredBooking.milestone_name && (
            <div style={{ fontSize: '0.8rem', opacity: 0.8, marginBottom: '8px' }}>
              {hoveredBooking.milestone_name}
            </div>
          )}
          <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>
            {hoveredBooking.hours_allocated/(Math.floor((new Date(hoveredBooking.end_datetime) - new Date(hoveredBooking.start_datetime)) / (1000 * 60 * 60 * 24)) + 1)}h per day
          </div>
        </div>
      )} */}

      {showBookingModal && (canCreateBookings || canEditBookings) && (
        <FormModal
          title={activeTab === "booking" ? (bookingForm.bookingId ? "Edit Booking" : "Add Booking") : "Request Time Off"}
          onCancel={() => {
            setShowBookingModal(false);
            setActiveTab("booking");
            setBookingForm({
              bookingId: null,
              employeeId: "",
              resourceId: "",
              startDate: "",
              endDate: "",
              projectId: "",
              projectName: "",
              projectCompanyName: "",
              milestoneId: "",
              activityTypeId: "",
              hoursPerDay: 8,
              description: "",
              isTimeSpecific: false,
              startTime: "09:00",
              endTime: "17:00",
            });
            setTimeOffForm({
              employee_id: '',
              timeOffTypeId: timeOffTypes.length > 0 ? timeOffTypes[0].leave_type_id : "",
              startDate: "",
              endDate: "",
              reason: "",
            });
          }}
          onSubmit={activeTab === "booking" ? handleBookingSubmit : handleTimeOffSubmit}
          tabs={[
            { key: "booking", label: "Booking" },
            { key: "timeOff", label: "Time Off" }
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          fields={activeTab === "booking" ? [
            ...(!isViewingOwnScheduleOnly && canViewAllSchedules && canEditBookings ? [{
              label: "Employee",
              type: "select",
              value: bookingForm.employeeId,
              onChange: (e) =>
                setBookingForm((prev) => ({ ...prev, employeeId: e.target.value, projectId: "" })),
              options: [
                { value: "", label: "Select employee..." },
                ...(employees || []).map((e) => ({
                  value: e.id,
                  label: e.name + (e.job_title ? ` (${e.job_title})` : ""),
                }))
              ],
            }] : []),
            {
              label: "Project Dates",
              type: "custom",
              render: () => (
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: '#6b7280' }}>Start Date</label>
                    <input
                      type="date"
                      value={bookingForm.startDate}
                      onChange={(e) =>
                        setBookingForm((prev) => ({ ...prev, startDate: e.target.value }))}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: '#6b7280' }}>End Date</label>
                    <input
                      type="date"
                      value={bookingForm.endDate}
                      onChange={(e) =>
                        setBookingForm((prev) => ({ ...prev, endDate: e.target.value }))}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              )
            },
            {
              label: "Project/Activity *",
              type: "custom",
              render: () => {
                const projectList = allProjects;
                const filteredProjectList = filteredAssignedProjects;
                const assignedCount = (projectList || []).filter((p) => p.isAssigned).length;
                const isDisabled = !bookingForm.employeeId || projectList.length === 0;

                // Determine placeholder text based on context
                const getPlaceholder = () => {
                  if (projectList.length === 0) return "No projects available";
                  if (!bookingForm.employeeId) return "Select employee first";
                  return "Select project...";
                };
                const placeholder = getPlaceholder();

                // If editing a booking with a project that's no longer in the active list (e.g. completed),
                // include it in the dropdown so the user can still see and manage it
                const bookingProjectInList = bookingForm.projectId && projectList?.some(
                  p => p.project_id?.toString() === bookingForm.projectId?.toString()
                );

                // Handler for opening the project assignment modal
                const handleOpenProjectAssignment = () => {
                  setShowProjectAssignmentModal(true);
                };

                return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: '#6b7280' }}>Company</label>
                    <CustomDropdown
                      options={bookingCompanyOptions}
                      value={companyFilter}
                      onChange={(val) => {
                        setCompanyFilter(val);
                        setBookingForm((prev) => ({
                          ...prev,
                          projectId: "",
                          projectName: "",
                          projectCompanyName: "",
                          milestoneId: "",
                        }));
                      }}
                      disabled={!bookingForm.employeeId || assignedCount === 0}
                      fullWidth
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: '#6b7280' }}>Project</label>
                      <CustomDropdown
                        options={[
                          { value: '', label: placeholder },
                          ...(bookingForm.employeeId && canManageProjectTeam
                            ? [{ value: 'assign-to-project', label: '➕ Assign to Project...' }]
                            : []),
                          ...(bookingForm.projectId && bookingForm.projectName && !bookingProjectInList
                            ? [{ value: bookingForm.projectId, label: `${bookingForm.projectName} (${bookingForm.projectCompanyName || 'No Client'}) — Completed` }]
                            : []),
                          ...(filteredProjectList.map((project) => ({
                            value: String(project.project_id),
                            label: `${project.project_name} (${project.client_company || 'No Client'})`,
                          })) || []),
                        ]}
                        value={bookingForm.projectId ? String(bookingForm.projectId) : ''}
                        onChange={(val) => {
                          if (val === 'assign-to-project') {
                            handleOpenProjectAssignment();
                          } else {
                            const project = (projectList || []).find(
                              (p) => String(p.project_id) === val
                            );
                            setBookingForm((prev) => ({
                              ...prev,
                              projectId: val,
                              projectName: project?.project_name || prev.projectName,
                              projectCompanyName: project?.client_company || "",
                              milestoneId: '',
                            }));
                          }
                        }}
                        disabled={isDisabled}
                        fullWidth
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75rem', color: '#6b7280' }}>Activity</label>
                      <CustomDropdown
                        options={[
                          { value: '', label: 'None' },
                          ...activityTypes.map((type) => ({
                            value: String(type.activity_type_id),
                            label: type.name,
                          })),
                        ]}
                        value={bookingForm.activityTypeId ? String(bookingForm.activityTypeId) : ''}
                        onChange={(val) => {
                          if (val === 'add-new-activity-type') {
                            setShowNewActivityTypeModal(true);
                          } else {
                            setBookingForm((prev) => ({
                              ...prev,
                              activityTypeId: val,
                            }));
                          }
                        }}
                        actions={[{ label: '➕ Add New Activity...', onClick: () => setShowNewActivityTypeModal(true) }]}
                        fullWidth
                      />
                  </div>
                </div>
                </div>
              );
              }
            },
            ...(bookingForm.employeeId ? [{
              label: "Book Resource (optional)",
              type: "select",
              value: bookingForm.resourceId,
              onChange: (e) =>
                setBookingForm((prev) => ({ ...prev, resourceId: e.target.value })),
              options: [
                { value: "", label: "No resource" },
                ...(resources || []).map((r) => {
                  return {
                    value: r.resource_id,
                    label: r.label,
                  };
                })
              ],
            }] : []),
            ...(() => {
              const milestoneList = employeeMilestones;
              if (milestoneList.length > 0) {
                return [{
                  label: "Milestone",
                  type: "select",
                  required: false,
                  value: bookingForm.milestoneId,
                  onChange: (e) =>
                    setBookingForm((prev) => ({
                      ...prev,
                      milestoneId: e.target.value,
                    })),
                  options: [
                    { value: "", label: "None" },
                    ...milestoneList.map((m) => ({
                      value: m.milestone_id,
                      label: `${m.milestone_name}`,
                    })),
                  ],
                }];
              }
              return [];
            })(),
            {
              label: "Time Allocation",
              type: "custom",
              render: () => {
                const calculateHours = (start, end) => {
                  if (!start || !end) return 0;
                  const [startH, startM] = start.split(':').map(Number);
                  const [endH, endM] = end.split(':').map(Number);
                  const startMinutes = startH * 60 + startM;
                  const endMinutes = endH * 60 + endM;
                  return Math.max(0, (endMinutes - startMinutes) / 60);
                };

                const hours = bookingForm.isTimeSpecific 
                  ? calculateHours(bookingForm.startTime, bookingForm.endTime)
                  : bookingForm.hoursPerDay;

                return (
                  <div style={{ 
                    background: '#f8fafc', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '12px', 
                    padding: '16px',
                    marginTop: '4px'
                  }}>
                    {/* Toggle Switch */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      marginBottom: '16px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button
                          type="button"
                          onClick={() => setBookingForm((prev) => ({ ...prev, isTimeSpecific: false }))}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '8px',
                            border: 'none',
                            background: !bookingForm.isTimeSpecific ? '#7c3aed' : '#e2e8f0',
                            color: !bookingForm.isTimeSpecific ? 'white' : '#64748b',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          Hours
                        </button>
                        <button
                          type="button"
                          onClick={() => setBookingForm((prev) => ({ ...prev, isTimeSpecific: true }))}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '8px',
                            border: 'none',
                            background: bookingForm.isTimeSpecific ? '#7c3aed' : '#e2e8f0',
                            color: bookingForm.isTimeSpecific ? 'white' : '#64748b',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          Specific Time
                        </button>
                      </div>
                      <div style={{
                        background: '#7c3aed',
                        color: 'white',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        minWidth: '52px',
                        textAlign: 'center'
                      }}>
                        {hours.toFixed(1)}h
                      </div>
                    </div>

                    {/* Time/Hours Input */}
                    {bookingForm.isTimeSpecific ? (
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                        <ClockTimePicker
                          label="Start Time"
                          value={bookingForm.startTime}
                          onChange={(val) => setBookingForm((prev) => ({ ...prev, startTime: val }))}
                        />
                        <div style={{ 
                          color: '#94a3b8', 
                          fontSize: '1.2rem',
                          paddingBottom: '12px'
                        }}>→</div>
                        <ClockTimePicker
                          label="End Time"
                          value={bookingForm.endTime}
                          onChange={(val) => setBookingForm((prev) => ({ ...prev, endTime: val }))}
                        />
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {/* Quick hour presets */}
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {[1, 2, 4, 6, 8].map((h) => (
                            <button
                              key={h}
                              type="button"
                              onClick={() => setBookingForm((prev) => ({ ...prev, hoursPerDay: h }))}
                              style={{
                                padding: '8px 16px',
                                borderRadius: '8px',
                                border: bookingForm.hoursPerDay === h ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                                background: bookingForm.hoursPerDay === h ? '#ede9fe' : 'white',
                                color: bookingForm.hoursPerDay === h ? '#7c3aed' : '#64748b',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                minWidth: '50px'
                              }}
                            >
                              {h}h
                            </button>
                          ))}
                        </div>
                        {/* Custom input with slider */}
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '16px',
                          background: 'white',
                          padding: '12px 16px',
                          borderRadius: '10px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>Custom:</span>
                          <div style={{ flex: 1, position: 'relative', padding: '0 10px' }}>
                            <div style={{
                              position: 'absolute',
                              top: '50%',
                              left: '10px',
                              right: '10px',
                              height: '6px',
                              transform: 'translateY(-50%)',
                              background: '#e2e8f0',
                              borderRadius: '3px',
                              overflow: 'hidden',
                              pointerEvents: 'none'
                            }}>
                              <div style={{
                                width: `${((bookingForm.hoursPerDay - 0.5) / 11.5) * 100}%`,
                                height: '100%',
                                background: 'linear-gradient(90deg, #7c3aed, #a78bfa)',
                                borderRadius: '3px'
                              }} />
                            </div>
                            <input
                              type="range"
                              min="0.5"
                              max="12"
                              step="0.5"
                              value={bookingForm.hoursPerDay}
                              onChange={(e) => setBookingForm((prev) => ({ ...prev, hoursPerDay: parseFloat(e.target.value) }))}
                              style={{
                                width: '100%',
                                height: '20px',
                                appearance: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                position: 'relative',
                                zIndex: 1,
                                margin: 0,
                                padding: 0
                              }}
                            />
                          </div>
                          <input
                            type="number"
                            min="0.5"
                            max="24"
                            step="0.5"
                            value={bookingForm.hoursPerDay}
                            onChange={(e) => setBookingForm((prev) => ({ ...prev, hoursPerDay: parseFloat(e.target.value) }))}
                            style={{
                              width: '65px',
                              padding: '8px 10px',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              fontSize: '0.9rem',
                              textAlign: 'center',
                              background: '#f8fafc',
                              fontWeight: 600,
                              color: '#7c3aed'
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              },
            },
            {
              label: "Description",
              type: "textarea",
              value: bookingForm.description,
              onChange: (e) =>
                setBookingForm((prev) => ({ ...prev, description: e.target.value })),
            },
          ].filter(Boolean) : [
            // Time Off fields
            (!isViewingOwnScheduleOnly && canViewAllSchedules && canEditTeamPendingTimeOff) && {
              label: "Employee",
              type: "select",
              value: timeOffForm.employee_id,
              onChange: (e) =>
                setTimeOffForm((prev) => ({ ...prev, employee_id: e.target.value })),
              options: [
                { value: "", label: "Select employee..." },
                ...(employees || []).map((e) => ({
                  value: e.id,
                  label: e.name + (e.job_title ? ` (${e.job_title})` : ""),
                }))
              ],
            },
            {
              label: "Time Off Type",
              type: "select",
              required: true,
              value: timeOffForm.timeOffTypeId,
              disabled: timeOffTypes.length === 0,
              onChange: (e) =>
                setTimeOffForm((prev) => ({ ...prev, timeOffTypeId: e.target.value })),
              options: (() => {
                if (timeOffTypes.length === 0) {
                  return [{ value: "", label: "Loading time off types..." }];
                }
                const opts = timeOffTypes.map(type => {
                  return {
                    value: String(type.leave_type_id || type.id || 'unknown'),
                    label: type.name || 'Unnamed'
                  };
                });
                return opts;
              })()
            },
            {
              label: "Project Dates", 
              type: "custom",
              render: () => renderTimeOffDatesField()
            },
            {
              label: "Reason",
              type: "textarea",
              value: timeOffForm.reason,
              onChange: (e) =>
                setTimeOffForm((prev) => ({ ...prev, reason: e.target.value })),
            },
          ]}
          error={null}
          footerActions={
            !!bookingForm.bookingId && canDeleteBookings && (
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <button
                  type="button"
                  className={styles.buttonDanger}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete Booking
                </button>
              </div>
            )
          }
        />
      )}

      {showEmployeeDetailModal && selectedEmployeeDetail && (
        <DetailsModal
          title="Employee Details"
          onClose={() => {
            setShowEmployeeDetailModal(false);
            setSelectedEmployeeDetail(null);
            setEmployeeAssignedProjects([]);
          }}
          tabs={[
            { key: 'overview', label: 'Overview' },
            { key: 'projects', label: 'Assigned Projects & Clients' },
          ]}
          activeTab={employeeDetailTab}
          onTabChange={setEmployeeDetailTab}
        >
          {employeeDetailTab === 'overview' && (
            <>
              <div className={detailStyles.detailSection}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{
                    width: '52px',
                    height: '52px',
                    borderRadius: '50%',
                    background: '#e2e8f0',
                    color: '#1f2937',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: '1rem',
                    overflow: 'hidden',
                    flexShrink: 0
                  }}>
                    {selectedEmployeeDetail.avatar_url ? (
                      <img
                        src={resolveUploadUrl(selectedEmployeeDetail.avatar_url)}
                        alt={selectedEmployeeDetail.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      getInitials(selectedEmployeeDetail.name)
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a' }}>
                      {selectedEmployeeDetail.name}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                      {selectedEmployeeDetail.job_title || 'No title'}
                    </div>
                  </div>
                </div>
              </div>

              {isEmployeeDetailLoading ? (
                <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading employee details...</div>
              ) : (
                <div className={detailStyles.detailSection}>
                  <div className={detailStyles.detailRow}>
                    <span className={detailStyles.detailLabel}>Email</span>
                    <span className={`${detailStyles.detailValue} ${!selectedEmployeeDetail.email ? detailStyles.empty : ''}`}>
                      {selectedEmployeeDetail.email || 'None'}
                    </span>
                  </div>
                  <div className={detailStyles.detailRow}>
                    <span className={detailStyles.detailLabel}>Phone</span>
                    <span className={`${detailStyles.detailValue} ${!selectedEmployeeDetail.phone_number ? detailStyles.empty : ''}`}>
                      {selectedEmployeeDetail.phone_number || 'None'}
                    </span>
                  </div>
                  <div className={detailStyles.detailRow}>
                    <span className={detailStyles.detailLabel}>Type</span>
                    <span className={detailStyles.detailValue}>
                      {selectedEmployeeDetail.kind === 'ghost' || selectedEmployeeDetail.status === 'placeholder' ? 'Contractor' : 'Employee'}
                    </span>
                  </div>
                  <div className={detailStyles.detailRow}>
                    <span className={detailStyles.detailLabel}>Hourly Rate</span>
                    <span className={`${detailStyles.detailValue} ${!selectedEmployeeDetail.default_hourly_rate ? detailStyles.empty : ''}`}>
                      {selectedEmployeeDetail.default_hourly_rate ? `${formatMoney(selectedEmployeeDetail.default_hourly_rate)}/hr` : 'None'}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}

          {employeeDetailTab === 'projects' && (
            <div className={detailStyles.detailSection}>
              {isEmployeeProjectsLoading ? (
                <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading assigned projects...</div>
              ) : employeeAssignedProjects.length === 0 ? (
                <div style={{ color: '#64748b', fontSize: '0.9rem' }}>No assigned projects found.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {employeeAssignedProjects.map((project) => (
                    <div
                      key={project.project_id}
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '10px',
                        padding: '10px 12px',
                        background: '#ffffff'
                      }}
                    >
                      <div style={{ fontSize: '0.92rem', fontWeight: 600, color: '#1f2937' }}>
                        {project.project_name}
                      </div>
                      <div style={{ marginTop: '4px', fontSize: '0.82rem', color: '#64748b' }}>
                        {project.company_name || 'No client company'}
                        {project.client_name ? ` • ${project.client_name}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DetailsModal>
      )}

      {showDeleteConfirm && (
        <ConfirmDialog
          message="Are you sure you want to delete this booking? This action cannot be undone."
          onConfirm={async () => {
            try {
              await deleteBooking(bookingForm.bookingId);
              setShowBookingModal(false);
              setShowDeleteConfirm(false);
              await fetchBookings();
              showToast('Booking deleted successfully', 'success');
            } catch (err) {
              console.error("Failed to delete booking", err);
              showToast(err.message || "Failed to delete booking", "error");
            }
          }}
          onCancel={() => setShowDeleteConfirm(false)}
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}

      {showTimeOffModal && (
        <FormModal
          title={timeOffForm.timeOffId ? "Edit Time Off Request" : "Request Time Off"}
          onCancel={() => {
            setShowTimeOffModal(false);
            setTimeOffForm({
              timeOffId: null,
              timeOffTypeId: "",
              startDate: "",
              endDate: "",
              reason: "",
              employee_id: '',
            });
          }}
          onSubmit={handleTimeOffSubmit}
          fields={[
            {
              label: "Employee",
              type: "select",
              value: timeOffForm.employee_id,
              onChange: (e) =>
                setTimeOffForm((prev) => ({ ...prev, employee_id: e.target.value })),
              options: (canApproveTimeOff || canViewAllTimeOff)
                ? [
                    { value: "", label: "Select employee..." },
                    ...(employees || []).map((e) => ({
                      value: e.id,
                      label: e.name + (e.job_title ? ` (${e.job_title})` : ""),
                    }))
                  ]
                : [{ value: timeOffForm.employee_id, label: currentUser.name + (currentUser.job_title ? ` (${currentUser.job_title})` : "") }],
            },
            {
              label: "Time Off Type",
              type: "select",
              required: true,
              value: timeOffForm.timeOffTypeId,
              disabled: timeOffTypes.length === 0,
              onChange: (e) =>
                setTimeOffForm((prev) => ({ ...prev, timeOffTypeId: e.target.value })),
              options: (() => {
                if (timeOffTypes.length === 0) {
                  return [{ value: "", label: "Loading time off types..." }];
                }
                const opts = timeOffTypes.map(type => {
                  return {
                    value: String(type.leave_type_id || type.id || 'unknown'),
                    label: type.name || 'Unnamed'
                  };
                });
                return opts;
              })()
            },
            {
              label: "Dates",
              type: "custom",
              render: () => renderTimeOffDatesField()
            },
            {
              label: "Reason",
              type: "textarea",
              value: timeOffForm.reason,
              onChange: (e) =>
                setTimeOffForm((prev) => ({ ...prev, reason: e.target.value })),
            },
          ]}
          error={null}
          footerActions={
            timeOffForm.timeOffId && (
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <button
                  type="button"
                  className={styles.buttonDanger}
                  onClick={() => {
                    setTimeOffToCancel(timeOffForm.timeOffId);
                    setShowCancelTimeOffConfirm(true);
                  }}
                >
                  Cancel Request
                </button>
              </div>
            )
          }
        />
      )}

      {showCancelTimeOffConfirm && (
        <ConfirmDialog
          message="Are you sure you want to cancel this time off request? This action cannot be undone."
          onConfirm={async () => {
            try {
              await cancelTimeOffRequest(timeOffToCancel);
              setShowTimeOffModal(false);
              setShowCancelTimeOffConfirm(false);
              setTimeOffToCancel(null);
              await fetchTimeOffRequests();
              showToast('Time off request cancelled successfully', 'success');
            } catch (err) {
              console.error("Failed to cancel time off request", err);
              showToast(err.message || "Failed to cancel time off request", "error");
            }
          }}
          onCancel={() => {
            setShowCancelTimeOffConfirm(false);
            setTimeOffToCancel(null);
          }}
          confirmText="Cancel Request"
          cancelText="Cancel"
        />
      )}

      {/* Time Off Approval Modal */}
      {showTimeOffApprovalModal && selectedTimeOffForApproval && (
        <div className={styles.timeOffApprovalOverlay} onClick={() => setShowTimeOffApprovalModal(false)}>
          <div className={styles.timeOffApprovalDialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.timeOffApprovalHeader}>
              <h3>Approve Time Off Request</h3>
              <button className={styles.timeOffApprovalCloseBtn} onClick={() => setShowTimeOffApprovalModal(false)}>×</button>
            </div>
            <div className={styles.timeOffApprovalBody}>
              <p><strong>Employee:</strong> {selectedTimeOffForApproval.employee_name}</p>
              <p><strong>Type:</strong> {selectedTimeOffForApproval.type_name}</p>
              <p><strong>Dates:</strong> {new Date(selectedTimeOffForApproval.start_date).toLocaleDateString()} - {new Date(selectedTimeOffForApproval.end_date).toLocaleDateString()}</p>
              <p><strong>Reason:</strong> {selectedTimeOffForApproval.reason || 'No reason provided'}</p>
            </div>
            <div className={styles.timeOffApprovalFooter}>
              <button className={`${styles.timeOffApprovalBtn} ${styles.deny}`} onClick={() => handleDenyTimeOff(selectedTimeOffForApproval.leave_request_id)}>Deny</button>
              <button className={`${styles.timeOffApprovalBtn} ${styles.accept}`} onClick={() => handleApproveTimeOff(selectedTimeOffForApproval.leave_request_id)}>Accept</button>
              <button className={`${styles.timeOffApprovalBtn} ${styles.edit}`} onClick={handleEditTimeOffApproval}>Edit</button>
              <button className={`${styles.timeOffApprovalBtn} ${styles.cancel}`} onClick={() => setShowTimeOffApprovalModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Modal */}
      {showNotificationsModal && (
        <div className={styles.timeOffApprovalOverlay} onClick={() => setShowNotificationsModal(false)}>
          <div className={styles.timeOffApprovalDialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.timeOffApprovalHeader}>
              <h3>Pending Time Off Requests</h3>
              <button className={styles.timeOffApprovalCloseBtn} onClick={() => setShowNotificationsModal(false)}>×</button>
            </div>
            <div className={styles.notificationsBody}>
              <ApprovalList
                requests={pendingTimeOffRequests}
                onApprove={handleApproveTimeOff}
                onDeny={handleDenyTimeOff}
                onEdit={handleEditTimeOffFromNotification}
              />
            </div>
          </div>
        </div>
      )}

      {/* New Activity Type Modal */}
      {showNewActivityTypeModal && (
        <FormModal
          title="Add New Activity"
          onCancel={() => {
            setShowNewActivityTypeModal(false);
            setNewActivityTypeForm({
              name: "",
              description: "",
              color: "#3b82f6"
            });
          }}
          onSubmit={(e) => {
            e.preventDefault();
            handleCreateActivityType();
          }}
          fields={[
            {
              label: "Activity Name",
              type: "custom",
              render: () => (
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      value={newActivityTypeForm.name}
                      onChange={(e) => setNewActivityTypeForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter activity name..."
                      style={{
                        width: '100%',
                        padding: '14px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        backgroundColor: 'white',
                        transition: 'border-color 0.15s ease'
                      }}
                      required
                    />
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem', color: '#6b7280' }}>Color</label>
                    <ColorPicker
                      value={newActivityTypeForm.color}
                      onChange={(e) => setNewActivityTypeForm(prev => ({ ...prev, color: e.target.value }))}
                    />
                  </div>
                </div>
              ),
            },
            {
              label: "Description",
              type: "textarea",
              value: newActivityTypeForm.description,
              onChange: (e) => setNewActivityTypeForm(prev => ({ ...prev, description: e.target.value })),
              placeholder: "Enter description (optional)...",
              required: false,
            },
          ]}
        />
      )}

      {/* Export Modal */}
      {showExportModal && (
        <FormModal
          title="Export Schedule to CSV"
          onCancel={() => setShowExportModal(false)}
          onSubmit={handleExportSubmit}
          fields={[
            {
              label: "Date Range",
              type: "custom",
              required: true,
              render: () => (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '2px', fontWeight: 500 }}>
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={exportForm.startDate}
                      onChange={(e) =>
                        setExportForm((prev) => ({ ...prev, startDate: e.target.value }))
                      }
                      style={{
                        width: '100%',
                        padding: '6px 10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '11px',
                        background: 'white',
                        color: '#334155',
                      }}
                    />
                  </div>
                  <div style={{ 
                    color: '#94a3b8', 
                    fontSize: '1.2rem',
                    paddingBottom: '6px'
                  }}>→</div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', marginBottom: '2px', fontWeight: 500 }}>
                      End Date
                    </label>
                    <input
                      type="date"
                      value={exportForm.endDate}
                      onChange={(e) =>
                        setExportForm((prev) => ({ ...prev, endDate: e.target.value }))
                      }
                      style={{
                        width: '100%',
                        padding: '6px 10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '11px',
                        background: 'white',
                        color: '#334155',
                      }}
                    />
                  </div>
                </div>
              ),
            },
            // Employee filter - only show if user can view all schedules
            canViewAllSchedules && {
              label: "Employee (optional)",
              type: "select",
              value: exportForm.employeeId,
              onChange: (e) =>
                setExportForm((prev) => ({ ...prev, employeeId: e.target.value })),
              options: [
                { value: "", label: "All employees" },
                ...(employees || []).map((e) => ({
                  value: e.id,
                  label: e.name + (e.job_title ? ` (${e.job_title})` : ""),
                }))
              ],
            },
            {
              label: "Project (optional)",
              type: "select",
              value: exportForm.projectId,
              onChange: (e) =>
                setExportForm((prev) => ({ ...prev, projectId: e.target.value })),
              options: [
                { value: "", label: "All projects" },
                ...Array.from(new Set(bookings.map(b => b.project_id)))
                  .filter(id => id)
                  .map(projectId => {
                    const booking = bookings.find(b => b.project_id === projectId);
                    return {
                      value: projectId,
                      label: booking?.project_name || `Project ${projectId}`
                    };
                  })
              ],
            },
            {
              label: "Group By (optional)",
              type: "select",
              value: exportForm.groupBy,
              onChange: (e) =>
                setExportForm((prev) => ({ ...prev, groupBy: e.target.value })),
              options: [
                { value: "", label: "No grouping" },
                { value: "company", label: "Client Company" },
                { value: "contact", label: "Client Name" }
              ],
            },
            {
              type: "custom",
              render: () => (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={exportForm.includeMilestones}
                    onChange={(e) =>
                      setExportForm((prev) => ({ ...prev, includeMilestones: e.target.checked }))
                    }
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#3a6aed',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#334155' }}>Include milestones in export</span>
                </label>
              ),
            },
            {
              type: "custom",
              render: () => (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={exportForm.includeActivityTypes}
                    onChange={(e) =>
                      setExportForm((prev) => ({ ...prev, includeActivityTypes: e.target.checked }))
                    }
                    style={{
                      width: '16px',
                      height: '16px',
                      accentColor: '#3a6aed',
                    }}
                  />
                  <span style={{ fontSize: '11px', color: '#334155' }}>Include activity types in export</span>
                </label>
              ),
            },
          ].filter(Boolean)}
        />
      )}

      {showVisibilityModal && (
        <FormModal
          title="Employee Visibility (Schedule)"
          onCancel={() => setShowVisibilityModal(false)}
          onSubmit={(e) => {
            e.preventDefault();
            setShowVisibilityModal(false);
          }}
          fields={[
            {
              type: "custom",
              render: () => (
                <div style={{ maxHeight: '360px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {isVisibilityLoading ? (
                    <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading employees...</div>
                  ) : visibilityEmployees.length === 0 ? (
                    <div style={{ color: '#64748b', fontSize: '0.9rem' }}>No employees found.</div>
                  ) : (
                    visibilityEmployees.map((employee) => (
                      <label
                        key={employee.employee_id}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                      >
                        <div>
                          <div style={{ fontSize: '0.9rem', fontWeight: 500, color: '#1e293b' }}>{employee.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{employee.job_title || 'No title'}</div>
                        </div>
                        <input
                          type="checkbox"
                          checked={!employee.hide_on_schedule}
                          onChange={(e) => handleScheduleVisibilityToggle(employee.employee_id, !e.target.checked)}
                        />
                      </label>
                    ))
                  )}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Project Assignment Modal */}
      {showProjectAssignmentModal && canManageProjectTeam && bookingForm.employeeId && (
        <ProjectAssignmentModal
          employeeId={bookingForm.employeeId}
          employeeName={(() => {
            const emp = employees.find(e => e.id?.toString() === bookingForm.employeeId?.toString());
            return emp?.name || '';
          })()}
          employeeJobTitle={(() => {
            const emp = employees.find(e => e.id?.toString() === bookingForm.employeeId?.toString());
            return emp?.job_title || '';
          })()}
          allProjects={allProjects}
          currentUser={currentUser}
          onAssigned={async (projectId) => {
            // Find the project to auto-populate the booking form
            const project = allProjects.find(p => p.project_id?.toString() === projectId?.toString());
            if (project) {
              setBookingForm((prev) => ({
                ...prev,
                projectId: project.project_id,
                projectName: project.project_name,
                projectCompanyName: project.client_company || '',
                milestoneId: '',
              }));
            }

            // Re-fetch projects to update isAssigned status
            try {
              const [allProjectsData, assignedProjectsData] = await Promise.all([
                getProjects(),
                getProjectsForEmployee(bookingForm.employeeId)
              ]);
              const allProjectsList = allProjectsData?.projects || allProjectsData || [];
              const assignedProjectsList = assignedProjectsData || [];
              const assignedProjectIds = new Set(assignedProjectsList.map(p => p.project_id?.toString()));
              const projectsWithAssignment = allProjectsList.map(p => ({
                ...p,
                isAssigned: assignedProjectIds.has(p.project_id?.toString())
              }));
              setAllProjects(projectsWithAssignment);
            } catch (err) {
              console.error("Failed to refresh projects after assignment", err);
            }
          }}
          onClose={() => setShowProjectAssignmentModal(false)}
          showToast={showToast}
        />
      )}
    </div>
  );
};

export default CanvasSchedule;
